import { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
  CheckCircle,
  X as XIcon,
  CurrencyDollar,
  Lightning,
  Brain,
  Sparkle,
  ArrowRight,
  Download,
  Image as ImageIcon,
  FileText,
  Package,
  ChartLineUp,
  Quotes,
  Megaphone,
  Target,
  WarningCircle,
  Robot,
  ChatCircleDots,
  HandHeart,
} from '@phosphor-icons/react';
import PrintableComparison from '../components/PrintableComparison';
import SalesConciergeFloat from '../components/SalesConciergeFloat';

/**
 * /affiliates — public landing page for the SalesCrunchCRM affiliate program.
 *
 * Structure (in order):
 *   1. Hero — $720/customer LTV is the headline
 *   2. The opportunity — math made explicit (1, 10, 50 referrals)
 *   3. Why SalesCrunchCRM converts — comparison vs HubSpot/Salesforce/Zoho
 *   4. How it works — 4 steps
 *   5. Media kit — downloadable logos, banners, swipes, full zip
 *   6. FAQ
 *   7. Final CTA + link to /affiliate-agreement legal page
 *
 * The Rewardful affiliate-signup URL is held in REWARDFUL_SIGNUP_URL — when
 * Chip wires the real Rewardful URL, just edit that constant.
 */

// Real Rewardful affiliate signup URL — verified against Chip's Rewardful
// dashboard (campaign "Friends of SalesCrunch CRM", 40% / 90-day cookie).
const REWARDFUL_SIGNUP_BASE = 'https://salescrunch-crm.getrewardful.com/signup';

// Default UTM tags applied when a visitor lands on /affiliates without their
// own campaign-specific UTMs (i.e., organic / direct traffic). When Lindy or
// anyone else runs outbound, they pass their own utm_* params on the inbound
// link (e.g., /affiliates?utm_source=lindy&utm_medium=cold_email) and those
// override these defaults — see buildApplyUrl().
const DEFAULT_UTMS = {
  utm_source: 'affiliates_page',
  utm_medium: 'organic',
  utm_campaign: 'apply_now',
};

/**
 * Build the Rewardful signup URL with UTM tags + click-path metadata propagated
 * from the current page's URL. Lets us measure where every signup came from
 * inside the Rewardful dashboard, not just total volume.
 *
 *   1. Reads window.location.search for any utm_*, ref, or gclid params
 *   2. Falls back to DEFAULT_UTMS for any utm_* the inbound URL didn't carry
 *   3. Adds utm_content=<clicked button id> so we can tell which CTA was used
 *      (hero, apply-now nav, or final-cta-bottom)
 *   4. Returns the fully-built URL — safe to drop straight into <a href>
 */
function buildApplyUrl(buttonId) {
  const out = new URL(REWARDFUL_SIGNUP_BASE);
  let inbound;
  try {
    inbound = new URLSearchParams(window.location.search);
  } catch {
    inbound = new URLSearchParams();
  }
  // Forward every utm_* and tracking param verbatim
  for (const key of ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'gclid', 'fbclid', 'ref']) {
    const v = inbound.get(key);
    if (v) out.searchParams.set(key, v);
  }
  // Fill in defaults for any UTM the inbound URL didn't carry
  for (const [key, val] of Object.entries(DEFAULT_UTMS)) {
    if (!out.searchParams.has(key)) out.searchParams.set(key, val);
  }
  // Always overwrite utm_content with the button identifier so we know
  // WHICH CTA on the page got clicked (top nav vs hero vs bottom cta)
  if (buttonId) out.searchParams.set('utm_content', buttonId);
  return out.toString();
}

// All downloadable assets live in /public/brand and are served at /brand/*.
const BRAND_BASE = '/brand';

export default function AffiliatesPage() {
  useEffect(() => {
    const prev = document.title;
    document.title =
      'Partner Hub — One link. Three products. 40% on everything | SalesCrunch';
    const desc =
      'Promote SalesCrunchCRM, MigrationCrunch, and VideoCrunch with one link and earn 40% on everything: $720 lifetime per CRM customer, $239.60 per migration, 40% of every VideoCrunch dollar. 90-day cookie. Apply free.';
    const upsert = (attr, key, content) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`);
      let created = false;
      let prevContent = null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
        created = true;
      } else {
        prevContent = el.getAttribute('content');
      }
      el.setAttribute('content', content);
      return () => {
        if (created) el.remove();
        else if (prevContent !== null) el.setAttribute('content', prevContent);
      };
    };
    const url = `${window.location.origin}/affiliates`;
    const restorers = [
      upsert('name', 'description', desc),
      upsert('property', 'og:title', document.title),
      upsert('property', 'og:description', desc),
      upsert('property', 'og:url', url),
      upsert('property', 'og:type', 'website'),
      upsert('property', 'og:site_name', 'SalesCrunchCRM'),
      upsert('name', 'twitter:card', 'summary_large_image'),
      upsert('name', 'twitter:title', document.title),
      upsert('name', 'twitter:description', desc),
    ];
    return () => {
      document.title = prev;
      restorers.forEach((fn) => fn());
    };
  }, []);

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="affiliates-page">
      {/* Nav */}
      <header className="border-b border-slate-200 bg-white sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="aff-nav-logo">
            <img
              src={`${BRAND_BASE}/salescrunchcrm-logo-horizontal-800.png`}
              alt="SalesCrunchCRM"
              className="h-9 w-auto"
            />
          </Link>
          <nav className="flex items-center gap-5 text-sm">
            <a href="#programs" className="text-slate-600 hover:text-[#0B5394] hidden sm:block" data-testid="aff-nav-programs">
              Programs
            </a>
            <a href="#opportunity" className="text-slate-600 hover:text-[#0B5394] hidden md:block">
              The opportunity
            </a>
            <a href="#pitch" className="text-slate-600 hover:text-[#0B5394] hidden md:block" data-testid="aff-nav-pitch">
              Sales toolkit
            </a>
            <a href="#kit" className="text-slate-600 hover:text-[#0B5394] hidden md:block">
              Media kit
            </a>
            <a href="#migrationcrunch-program" className="text-slate-600 hover:text-[#0B5394] hidden md:block" data-testid="aff-nav-mc-program">
              MigrationCrunch
            </a>
            <a href="#videocrunch-program" className="text-slate-600 hover:text-[#0B5394] hidden md:block" data-testid="aff-nav-vcx-program">
              VideoCrunch
            </a>
            <a href="#faq" className="text-slate-600 hover:text-[#0B5394] hidden sm:block">
              FAQ
            </a>
            <a
              href={buildApplyUrl('nav_apply')}
              className="bg-[#F39C12] hover:bg-[#E08C00] text-black font-bold rounded-md px-4 py-2 text-sm transition-colors"
              data-testid="aff-nav-cta"
            >
              Apply now
            </a>
          </nav>
        </div>
      </header>

      {/* PROGRAM SELECTOR — three cards, one program: SalesCrunchCRM,
          MigrationCrunch, and VideoCrunch are ALL live at a unified 40%.
          One Rewardful link covers every product (same Stripe account), so
          cross-pollination is automatic: a customer who arrives for a free
          VideoCrunch video and later buys a $599 migration credits the same
          affiliate. Chip approved flat 40% on everything, Jun 2026. */}
      <section id="programs" className="bg-slate-50 border-b border-slate-200 px-6 py-10" data-testid="aff-program-selector">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-6">
            <div className="text-[10px] uppercase tracking-widest font-black text-slate-500 mb-1">
              Three products · One link · One application
            </div>
            <h2 className="text-2xl md:text-3xl font-black text-slate-900">
              40% commission on <span className="text-[#F39C12]">everything</span> we sell.
            </h2>
            <p className="text-sm text-slate-600 mt-2 max-w-2xl mx-auto">
              Your single referral link tracks all three products. Whichever one
              your audience buys first — you earn on everything they buy after.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {/* SalesCrunchCRM — LIVE */}
            <a
              href="#salescrunchcrm-program"
              className="group relative bg-gradient-to-br from-[#0B5394] to-[#073763] text-white rounded-xl p-6 shadow-md hover:shadow-xl transition-all border-2 border-transparent hover:border-[#F39C12]/60"
              data-testid="aff-program-card-sc"
            >
              <div className="flex items-center gap-2 mb-3">
                <span className="relative flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-400"></span>
                </span>
                <span className="text-[10px] uppercase tracking-widest font-black text-emerald-300">
                  Live · 40% recurring
                </span>
              </div>
              <img
                src={`${BRAND_BASE}/salescrunchcrm-logo-horizontal-800.png`}
                alt="SalesCrunchCRM"
                className="h-7 w-auto mb-4 opacity-95"
              />
              <div className="text-lg md:text-xl font-black leading-tight mb-2">
                Earn <span className="text-[#F39C12]">$720 per customer</span>
              </div>
              <p className="text-sm text-white/80 leading-relaxed mb-4">
                $30/user/mo CRM. 40% of every invoice, paid monthly for
                60 months. The recurring backbone of your income.
              </p>
              <div className="flex items-center gap-1.5 text-sm font-bold text-[#F39C12] group-hover:gap-3 transition-all">
                See the full program <ArrowRight size={14} weight="bold" />
              </div>
            </a>

            {/* MigrationCrunch — LIVE */}
            <a
              href="#migrationcrunch-program"
              className="group relative bg-gradient-to-br from-[#0F0F1A] to-[#1a1a2e] text-white rounded-xl p-6 shadow-md hover:shadow-xl transition-all border-2 border-transparent hover:border-emerald-400/60"
              data-testid="aff-program-card-mc"
            >
              <div className="flex items-center gap-2 mb-3">
                <span className="relative flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-400"></span>
                </span>
                <span className="text-[10px] uppercase tracking-widest font-black text-emerald-300">
                  Live · 40% per sale
                </span>
              </div>
              <img
                src={`${BRAND_BASE}/migrationcrunch-logo-horizontal-400.png`}
                alt="MigrationCrunch"
                className="h-7 w-auto mb-4 opacity-95"
              />
              <div className="text-lg md:text-xl font-black leading-tight mb-2">
                Earn <span className="text-emerald-400">$239.60 per migration</span>
              </div>
              <p className="text-sm text-white/70 leading-relaxed mb-4">
                One-time $599 AI CRM migration with patent-pending note
                extraction. Big single payouts — and migrated customers usually
                subscribe to the CRM next.
              </p>
              <div className="flex items-center gap-1.5 text-sm font-bold text-emerald-400 group-hover:gap-3 transition-all">
                See the program <ArrowRight size={14} weight="bold" />
              </div>
            </a>

            {/* VideoCrunch — LIVE */}
            <a
              href="#videocrunch-program"
              className="group relative bg-gradient-to-br from-[#0a0e27] to-[#101636] text-white rounded-xl p-6 shadow-md hover:shadow-xl transition-all border-2 border-transparent hover:border-[#F39C12]/60"
              data-testid="aff-program-card-vcx"
            >
              <div className="flex items-center gap-2 mb-3">
                <span className="relative flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-400"></span>
                </span>
                <span className="text-[10px] uppercase tracking-widest font-black text-emerald-300">
                  Live · 40% of everything
                </span>
              </div>
              <div className="text-base font-black mb-4 tracking-tight">
                Video<span className="text-[#F39C12]">Crunch</span>
              </div>
              <div className="text-lg md:text-xl font-black leading-tight mb-2">
                The <span className="text-[#F39C12]">easiest referral</span> in SaaS
              </div>
              <p className="text-sm text-white/70 leading-relaxed mb-4">
                AI tutorial videos — first one free, no credit card. You earn
                40% of the $49/mo plan AND every credit pack they ever buy.
              </p>
              <div className="flex items-center gap-1.5 text-sm font-bold text-[#F39C12] group-hover:gap-3 transition-all">
                See the program <ArrowRight size={14} weight="bold" />
              </div>
            </a>
          </div>
        </div>
      </section>

      {/* HERO — SalesCrunchCRM program */}
      <section id="salescrunchcrm-program" className="bg-gradient-to-b from-[#0B5394] to-[#073763] text-white">
        <div className="max-w-5xl mx-auto px-6 py-20 md:py-28 text-center">
          <div className="inline-flex items-center gap-1.5 text-[10px] font-bold tracking-widest text-[#F39C12] uppercase mb-4">
            <Sparkle size={12} weight="fill" /> SalesCrunchCRM Affiliate Program
          </div>
          <h1 className="text-4xl md:text-6xl font-black leading-tight mb-5">
            Earn{' '}
            <span className="text-[#F39C12]">$720 per customer</span>{' '}
            you refer.
          </h1>
          <p className="text-lg md:text-2xl text-white/85 max-w-3xl mx-auto leading-relaxed mb-3">
            40% recurring commission, paid every month for{' '}
            <strong className="text-white">60 months</strong>. Promote a CRM
            people actually want to buy.
          </p>
          <p className="text-sm text-white/65 max-w-2xl mx-auto">
            $30/user/month pricing · No learning curve · Built-in AI sales
            agent · 90-day attribution cookie
          </p>
          <div className="mt-9 flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href={buildApplyUrl('hero_apply')}
              className="px-7 py-3.5 bg-[#F39C12] hover:bg-[#E08C00] text-black font-black rounded-md inline-flex items-center justify-center gap-2"
              data-testid="aff-hero-apply"
            >
              Apply free — get your link <ArrowRight size={16} weight="bold" />
            </a>
            <a
              href="#opportunity"
              className="px-7 py-3.5 border border-white/30 hover:border-white/60 text-white font-bold rounded-md inline-flex items-center justify-center gap-2"
              data-testid="aff-hero-math"
            >
              See the math
            </a>
          </div>
          <p className="text-xs text-white/50 mt-6">
            Powered by Rewardful · Payouts via PayPal or Wise · Net-30
          </p>
        </div>
      </section>

      {/* THE OPPORTUNITY — explicit math */}
      <section id="opportunity" className="px-6 py-20 bg-slate-50 border-b border-slate-200">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-1.5 text-[10px] font-bold tracking-widest text-[#F39C12] uppercase mb-3">
              <CurrencyDollar size={12} weight="fill" /> The math
            </div>
            <h2 className="text-3xl md:text-5xl font-black mb-4 text-slate-900">
              $30/user/mo × 40% × 60 months = <span className="text-[#0B5394]">$720 per customer</span>
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              We pay 40% recurring for the entire first 60 months of every
              customer&apos;s subscription. That&apos;s not a one-time bounty — it&apos;s
              real, monthly, compounding income.
            </p>
          </div>

          {/* Earnings ladder */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-14">
            <EarnCard count="1" total="$720" sub="per customer over 60 mo" data="aff-earn-1" />
            <EarnCard count="10" total="$7,200" sub="lifetime per cohort of 10" data="aff-earn-10" />
            <EarnCard count="50" total="$36,000" sub="lifetime per cohort of 50" highlight data="aff-earn-50" />
            <EarnCard count="100" total="$72,000" sub="lifetime per cohort of 100" data="aff-earn-100" />
          </div>

          <p className="text-center text-xs text-slate-500 italic max-w-2xl mx-auto">
            Math assumes a customer stays subscribed at $30/user/mo for the full
            60-month commission window. Real-world average is shorter, but
            churn-adjusted lifetime payout is still industry-leading for SaaS.
          </p>
        </div>
      </section>

      {/* WHY THIS CONVERTS — comparison + value props */}
      <section className="px-6 py-20 border-b border-slate-200">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-1.5 text-[10px] font-bold tracking-widest text-[#F39C12] uppercase mb-3">
              <ChartLineUp size={12} weight="fill" /> Why this converts
            </div>
            <h2 className="text-3xl md:text-5xl font-black mb-4 text-slate-900">
              The CRM industry has a <span className="text-[#0B5394]">trust problem.</span>
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto text-base md:text-lg leading-relaxed">
              The word on the street: people are <em>fed up</em> with slow,
              bloated, overpriced CRMs they&apos;re forced to use but never want to
              open. We built the opposite of that — and your audience can feel
              the difference within 60 seconds of opening it.
            </p>
          </div>

          {/* Printable side-by-side comparison — affiliates can hit Print/Email/Copy
              from the toolbar to grab a one-pager they hand to prospects. */}
          <PrintableComparison
            variant="light"
            testid="aff-compare"
            title="What you're actually selling"
            subtitle="Side-by-side vs the 3 CRMs your audience is most likely already paying for."
            shareSubject="SalesCrunchCRM vs HubSpot / Salesforce / Zoho — affiliate one-pager"
            columns={[
              { id: 'sc',  label: 'SalesCrunchCRM',     sub: '$30/user/mo',                   isUs: true },
              { id: 'hs',  label: 'HubSpot Sales Pro',  sub: 'Marketing-first'                            },
              { id: 'sf',  label: 'Salesforce',         sub: 'Enterprise default'                         },
              { id: 'zo',  label: 'Zoho CRM',           sub: 'SMB low-cost'                               },
            ]}
            rows={COMPARE_ROWS.map((row) => ({
              label: row.label,
              cells: {
                sc: <Cell mode={row.us.mode} text={row.us.text} winner />,
                hs: <Cell mode={row.hubspot.mode} text={row.hubspot.text} />,
                sf: <Cell mode={row.salesforce.mode} text={row.salesforce.text} />,
                zo: <Cell mode={row.zoho.mode} text={row.zoho.text} />,
              },
            }))}
            footnote="Pricing reflects publicly listed rates as of Feb 2026. Hit Print to save as PDF — or Email to send a prospect this comparison instantly."
          />

          {/* Pillar cards */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mt-10">
            <Pillar
              icon={<Lightning size={26} weight="fill" />}
              tone="amber"
              title="Zero learning curve"
              body="If your audience ever used ACT! 4.0, they already know how to use SalesCrunchCRM. No 4-week onboarding, no certifications."
            />
            <Pillar
              icon={<CurrencyDollar size={26} weight="fill" />}
              tone="emerald"
              title="$30/user/mo"
              body="No upsells, no per-seat math, no enterprise quote. The price on the page is the price your referrals actually pay."
            />
            <Pillar
              icon={<Brain size={26} weight="fill" />}
              tone="purple"
              title="Killer AI agent built in"
              body="Crunchy drafts personalized follow-ups, finds stale leads, and runs bulk-with-approval email campaigns. Not a chatbot — a real workflow tool."
            />
            <Pillar
              icon={<Sparkle size={26} weight="fill" />}
              tone="blue"
              title="Fast at scale"
              body="250,000 contacts and the page still loads instantly. The big-name CRMs slow to a crawl past 50k records."
            />
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="px-6 py-20 bg-slate-50 border-b border-slate-200">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-5xl font-black mb-4 text-slate-900">
              How it works
            </h2>
            <p className="text-slate-600">
              No phone calls, no application gauntlet. You can be live in
              under 10 minutes.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Step n="1" title="Sign up" body="Free application via Rewardful. Most accounts approved same-day." />
            <Step n="2" title="Get your link" body="Unique tracking URL + dashboard with real-time clicks, signups, and pending commissions." />
            <Step n="3" title="Promote" body="Grab logos, banners, and email swipes from the kit below. Or write your own." />
            <Step n="4" title="Earn" body="40% of every referred subscription, paid monthly, for 60 months. Net-30 to PayPal or Wise." />
          </div>
        </div>
      </section>

      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      {/*  SALES TOOLKIT — 4 sub-sections of copy/pitch material affiliates  */}
      {/*  can lift verbatim. Source files in /app/marketing/*.md. Visual    */}
      {/*  styling kept consistent with the rest of the page: blue + amber  */}
      {/*  palette, slate-50/white alternating, slate-200 borders.          */}
      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}

      {/* 1. ONE-PAGE PITCH ─────────────────────────────────────────────── */}
      <section id="pitch" className="px-6 py-20 border-b border-slate-200 bg-white" data-testid="aff-pitch-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-1.5 text-[10px] font-bold tracking-widest text-[#F39C12] uppercase mb-3">
              <Megaphone size={12} weight="fill" /> Steal our pitch
            </div>
            <h2 className="text-3xl md:text-5xl font-black mb-4 text-slate-900">
              The pitch, copy-paste ready.
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Three hooks, the three-line pitch, who&apos;s in the bullseye,
              the numbers that close it, and the four objections you&apos;ll hear most.
              Use any of it, all of it, none of it.
            </p>
          </div>

          {/* Hero headline alternatives */}
          <div className="grid md:grid-cols-3 gap-4 mb-10">
            {[
              'The CRM for people who escaped ACT! — and can\u2019t stomach Salesforce.',
              'You loved ACT! 3.0. You hate the 2026 \u201Centerprise\u201D replacements. Meet SalesCrunchCRM.',
              'Fast. Familiar. Built for people who just want their contacts back.',
            ].map((h, i) => (
              <div
                key={i}
                className="bg-slate-50 border border-slate-200 rounded-lg p-5 hover:border-[#F39C12] hover:shadow-md transition-all"
                data-testid={`aff-pitch-headline-${i + 1}`}
              >
                <div className="text-[10px] uppercase tracking-widest text-[#F39C12] font-black mb-2">
                  Headline {i + 1}
                </div>
                <p className="text-base md:text-lg font-black text-slate-900 leading-snug">
                  &ldquo;{h}&rdquo;
                </p>
              </div>
            ))}
          </div>

          {/* 3-line pitch — pull quote */}
          <div className="relative bg-gradient-to-br from-[#0B5394] to-[#073763] text-white rounded-lg p-8 md:p-10 shadow-xl mb-12" data-testid="aff-pitch-3line">
            <Quotes size={48} weight="fill" className="text-[#F39C12]/30 absolute top-5 left-5" />
            <div className="relative z-10 max-w-3xl mx-auto">
              <div className="text-[10px] uppercase tracking-widest text-[#F39C12] font-black mb-3 text-center">
                The 3-line pitch
              </div>
              <p className="text-lg md:text-2xl font-bold leading-snug text-center">
                If you used ACT! in the &apos;90s or 2000s and miss how simple and fast it was:
                <span className="text-[#F39C12]"> SalesCrunchCRM is the exact same layout and workflow</span> —
                updated for the cloud, with real-time collaboration, global search, and email sending built in.
                It opens in your browser. It syncs automatically. It costs $30 per user per month.
              </p>
            </div>
          </div>

          {/* Who it's for / not for */}
          <div className="grid md:grid-cols-2 gap-5 mb-12">
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-6" data-testid="aff-pitch-who-for">
              <div className="flex items-center gap-2 mb-4">
                <Target size={20} weight="fill" className="text-emerald-700" />
                <h3 className="font-black text-base text-emerald-900">Bullseye customers</h3>
              </div>
              <ul className="space-y-2.5 text-sm text-slate-800">
                {[
                  'Insurance agents, financial advisors, real-estate pros, consultants — anyone whose relationships ARE their business',
                  'Solo operators and small teams (1\u201325 people)',
                  'ACT! refugees who abandoned the new "ACT! Growth Suite" because it\u2019s a 400-page learning curve',
                  'Salesforce dropouts — the 80% of small businesses paying $150/seat for features they don\u2019t use',
                ].map((t, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <CheckCircle size={16} weight="fill" className="text-emerald-600 flex-shrink-0 mt-0.5" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-rose-50 border border-rose-200 rounded-lg p-6" data-testid="aff-pitch-who-not-for">
              <div className="flex items-center gap-2 mb-4">
                <XIcon size={20} weight="bold" className="text-rose-600" />
                <h3 className="font-black text-base text-rose-900">Don&apos;t pitch them</h3>
              </div>
              <ul className="space-y-2.5 text-sm text-slate-800">
                {[
                  'Enterprises that need 47 custom objects and SOC2 Type II audits',
                  'Agencies running 50-step automated drip campaigns',
                  '\u201CGrowth hackers\u201D who live in HubSpot workflows',
                ].map((t, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <XIcon size={14} weight="bold" className="text-rose-500 flex-shrink-0 mt-0.5" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Key numbers strip */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-12" data-testid="aff-pitch-numbers">
            {[
              { stat: '250k', label: 'Contacts, instant search', icon: <Lightning size={16} weight="fill" /> },
              { stat: '8', label: 'Boolean operators', icon: <Target size={16} weight="fill" /> },
              { stat: 'Live', label: 'Real-time team sync', icon: <Sparkle size={16} weight="fill" /> },
              { stat: '3,000', label: 'Emails/mo included', icon: <ChartLineUp size={16} weight="fill" /> },
              { stat: '20+', label: 'Features that matter', icon: <CheckCircle size={16} weight="fill" /> },
              { stat: '$30', label: 'Per user / month', icon: <CurrencyDollar size={16} weight="fill" /> },
            ].map((n, i) => (
              <div key={i} className="bg-slate-900 text-white rounded-lg p-4 text-center">
                <div className="text-[#F39C12] mb-2 flex justify-center">{n.icon}</div>
                <div className="text-2xl md:text-3xl font-black mb-1">{n.stat}</div>
                <div className="text-[10px] text-white/60 uppercase tracking-wider leading-tight">{n.label}</div>
              </div>
            ))}
          </div>

          {/* Objection crushers */}
          <div className="text-center mb-6">
            <h3 className="text-xl md:text-2xl font-black text-slate-900">
              The four objections you&apos;ll hear most.
            </h3>
            <p className="text-sm text-slate-500 mt-1">Memorize these. They close deals.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {OBJECTIONS.map((o, i) => (
              <div
                key={i}
                className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm"
                data-testid={`aff-pitch-obj-${i + 1}`}
              >
                <div className="text-[10px] uppercase tracking-widest text-rose-500 font-black mb-2">
                  They say
                </div>
                <p className="font-bold text-slate-900 text-base mb-3 italic">
                  &ldquo;{o.q}&rdquo;
                </p>
                <div className="border-l-2 border-[#F39C12] pl-3">
                  <div className="text-[10px] uppercase tracking-widest text-[#F39C12] font-black mb-1.5">
                    You say
                  </div>
                  <p className="text-sm text-slate-700 leading-relaxed">{o.a}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 2. 7 ACT! ISSUES ───────────────────────────────────────────────── */}
      <section id="vs-act" className="px-6 py-20 border-b border-slate-200 bg-slate-50" data-testid="aff-vs-act-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-1.5 text-[10px] font-bold tracking-widest text-[#F39C12] uppercase mb-3">
              <WarningCircle size={12} weight="fill" /> Vs ACT!
            </div>
            <h2 className="text-3xl md:text-5xl font-black mb-4 text-slate-900">
              7 problems ACT! users complain about.
              <br />
              <span className="text-[#0B5394]">We don&apos;t have any of them.</span>
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Every &ldquo;7 common ACT! issues&rdquo; article on Google lists the same
              seven complaints — they&apos;re all symptoms of one architectural
              decision ACT! made in 1996. We made the opposite one.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            {ACT_ISSUES.map((issue, i) => (
              <div
                key={i}
                className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm"
                data-testid={`aff-act-issue-${i + 1}`}
              >
                {/* Red header — the ACT! problem */}
                <div className="bg-rose-50 border-b border-rose-200 px-5 py-3 flex items-start gap-3">
                  <div className="w-7 h-7 rounded-full bg-rose-500 text-white font-black text-sm flex items-center justify-center flex-shrink-0">
                    {i + 1}
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-rose-600 font-black mb-0.5">
                      ACT! problem
                    </div>
                    <h3 className="text-sm font-black text-slate-900 leading-snug">{issue.problem}</h3>
                  </div>
                </div>
                {/* Green answer — the SC fix */}
                <div className="px-5 py-4 bg-white">
                  <div className="text-[10px] uppercase tracking-widest text-emerald-600 font-black mb-2 inline-flex items-center gap-1">
                    <CheckCircle size={11} weight="fill" /> Why SalesCrunch doesn&apos;t have it
                  </div>
                  <p className="text-sm text-slate-700 leading-relaxed mb-3">{issue.fix}</p>
                  <div className="bg-amber-50 border-l-2 border-[#F39C12] px-3 py-2 rounded-r">
                    <div className="text-[9px] uppercase tracking-widest text-[#F39C12] font-black mb-0.5">
                      Talking point
                    </div>
                    <p className="text-xs text-slate-800 italic leading-relaxed">&ldquo;{issue.talkingPoint}&rdquo;</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <p className="text-center text-sm text-slate-600 max-w-2xl mx-auto mt-10">
            <span className="font-bold text-slate-900">Bottom line:</span> these
            seven problems all come from ACT! making the customer run a Windows
            SQL database. We removed that. The problems went with it.
          </p>
        </div>
      </section>

      {/* 3. FEATURE CATALOG ─────────────────────────────────────────────── */}
      <section id="features" className="px-6 py-20 border-b border-slate-200 bg-white" data-testid="aff-features-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-1.5 text-[10px] font-bold tracking-widest text-[#F39C12] uppercase mb-3">
              <Lightning size={12} weight="fill" /> Feature catalog
            </div>
            <h2 className="text-3xl md:text-5xl font-black mb-4 text-slate-900">
              23 features that matter.
              <br />
              <span className="text-[#0B5394]">Zero you&apos;ll never use.</span>
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              The everything-in-one-place reference. Pitch any one of these as
              the &ldquo;hook&rdquo; for the niche you&apos;re writing to.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {FEATURES.map((f, i) => (
              <div
                key={i}
                className="bg-slate-50 hover:bg-white border border-slate-200 hover:border-[#0B5394] rounded-md p-4 transition-colors group"
                data-testid={`aff-feature-${i + 1}`}
              >
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-md bg-[#0B5394] text-white font-black text-xs flex items-center justify-center flex-shrink-0 group-hover:bg-[#F39C12] group-hover:text-black transition-colors">
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-black text-sm text-slate-900 mb-1 leading-snug">{f.name}</h3>
                    <p className="text-[12px] text-slate-600 leading-relaxed">{f.benefit}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Removed-bloat callout */}
          <div className="mt-10 bg-slate-900 text-white rounded-lg p-6 md:p-8" data-testid="aff-features-removed">
            <div className="grid md:grid-cols-2 gap-6 items-center">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[#F39C12] font-black mb-2">
                  Bloat we deliberately left out
                </div>
                <h3 className="text-xl md:text-2xl font-black mb-2">
                  No &ldquo;features&rdquo; you&apos;d need a consultant to configure.
                </h3>
                <p className="text-sm text-white/70 leading-relaxed">
                  Salesforce and HubSpot users notice the absence within five
                  minutes — and they&apos;re relieved.
                </p>
              </div>
              <ul className="space-y-2 text-sm">
                {[
                  'Opportunity scoring you need a consultant to configure',
                  '47-field custom object builder',
                  '$30/mo per-seat plugin marketplace',
                  'Workflow rules that take a week to set up',
                  'Einstein AI you didn\u2019t ask for',
                ].map((t, i) => (
                  <li key={i} className="flex items-start gap-2 text-white/90">
                    <XIcon size={14} weight="bold" className="text-rose-400 flex-shrink-0 mt-0.5" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* 4. WHY CRUNCHY IS DIFFERENT ────────────────────────────────────── */}
      <section id="crunchy" className="px-6 py-20 border-b border-slate-200 bg-gradient-to-b from-[#0a0e27] via-[#0F0F1A] to-[#0a0e27] text-white" data-testid="aff-crunchy-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-1.5 text-[10px] font-bold tracking-widest text-[#F39C12] uppercase mb-3">
              <Robot size={12} weight="fill" /> Meet Crunchy
            </div>
            <h2 className="text-3xl md:text-5xl font-black mb-4">
              The AI sidekick competitors
              <br />
              <span className="text-[#F39C12]">can&apos;t copy.</span>
            </h2>
            <p className="text-white/70 max-w-2xl mx-auto">
              Most CRMs bolted on a chatbot, named it something generic, and fed
              it the same internet ChatGPT already has. Crunchy is different.
              Here&apos;s exactly how.
            </p>
          </div>

          {/* Example side-by-side — the showstopper */}
          <div className="bg-white/5 border border-white/10 rounded-lg p-6 md:p-8 mb-10 backdrop-blur-sm" data-testid="aff-crunchy-example">
            <div className="text-center mb-6">
              <div className="text-[10px] uppercase tracking-widest text-[#F39C12] font-black mb-2">
                Example
              </div>
              <p className="text-base md:text-lg text-white/90 font-bold italic">
                User: &ldquo;I can&apos;t type in the contact field — the spacebar doesn&apos;t work.&rdquo;
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-5">
              <div className="bg-rose-950/40 border border-rose-500/30 rounded-md p-5">
                <div className="text-[10px] uppercase tracking-widest text-rose-300 font-black mb-2 inline-flex items-center gap-1">
                  <XIcon size={11} weight="bold" /> Generic CRM AI
                </div>
                <p className="text-sm text-white/80 italic leading-relaxed">
                  &ldquo;Sorry to hear that! Click &lsquo;Need a human?&rsquo; below.&rdquo;
                </p>
              </div>
              <div className="bg-emerald-950/40 border border-emerald-500/30 rounded-md p-5">
                <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-black mb-2 inline-flex items-center gap-1">
                  <CheckCircle size={11} weight="fill" /> Crunchy
                </div>
                <p className="text-sm text-white/90 italic leading-relaxed">
                  &ldquo;Weird — let&apos;s narrow this down. Does the spacebar work in
                  the Company field right below Contact? If yes, this is isolated
                  to that one input — likely a controlled-input bug. Quick
                  workaround: type the name in Company, cut, paste into Contact.
                  Either way, click &lsquo;Need a human?&rsquo; and I&apos;ll make sure they get the full picture.&rdquo;
                </p>
              </div>
            </div>
            <p className="text-center text-xs text-white/50 mt-5 italic">
              The difference between AI that talks at you and AI that thinks with you.
            </p>
          </div>

          {/* 7 differentiators */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {CRUNCHY_DIFFS.map((d, i) => {
              const Icon = d.icon;
              return (
                <div
                  key={i}
                  className="bg-white/5 border border-white/10 hover:border-[#F39C12]/50 rounded-lg p-5 transition-colors"
                  data-testid={`aff-crunchy-diff-${i + 1}`}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-9 h-9 rounded-md bg-[#F39C12] text-black flex items-center justify-center flex-shrink-0">
                      <Icon size={18} weight="bold" />
                    </div>
                    <div className="text-xs font-black text-[#F39C12] uppercase tracking-widest">
                      #{i + 1}
                    </div>
                  </div>
                  <h3 className="text-base font-black mb-2 leading-snug">{d.title}</h3>
                  <p className="text-sm text-white/70 leading-relaxed">{d.body}</p>
                </div>
              );
            })}
          </div>

          {/* Autonomous agents — built in, not bolted on */}
          <div className="mt-10 bg-white/5 border border-white/10 rounded-lg p-6 md:p-8" data-testid="aff-crunchy-agents">
            <div className="text-center mb-6">
              <div className="text-[10px] uppercase tracking-widest text-[#F39C12] font-black mb-2">
                New · Autonomous agents — built in, not bolted on
              </div>
              <p className="text-sm text-white/70 max-w-2xl mx-auto">
                VC-backed startups charge $100s/month to wire third-party
                &ldquo;AI employees&rdquo; onto clunky CRMs. SalesCrunch ships
                them inside the $30/user/mo workspace.
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-5">
              <div className="bg-white/5 border border-white/10 rounded-md p-5" data-testid="aff-agent-brief">
                <h3 className="text-base font-black mb-1.5 text-[#F39C12]">Pre-Call Briefs</h3>
                <p className="text-sm text-white/70 leading-relaxed">
                  Open a contact with a meeting today and Crunchy has already
                  read the file — who they are, where you left off, and a
                  suggested opening line, right on the contact card.
                </p>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-md p-5" data-testid="aff-agent-ghostbuster">
                <h3 className="text-base font-black mb-1.5 text-[#F39C12]">GhostBuster</h3>
                <p className="text-sm text-white/70 leading-relaxed">
                  Deals with no touch in 14 days get flagged on the pipeline,
                  and Crunchy drafts the re-engagement email from your own
                  notes. One click to the composer. No &ldquo;just checking in.&rdquo;
                </p>
              </div>
            </div>
          </div>

          {/* The Crunchy promise */}
          <div className="mt-10 bg-[#F39C12]/10 border border-[#F39C12]/30 rounded-lg p-6 md:p-8 text-center" data-testid="aff-crunchy-promise">
            <div className="text-[10px] uppercase tracking-widest text-[#F39C12] font-black mb-3">
              The Crunchy promise
            </div>
            <div className="grid md:grid-cols-3 gap-5 text-left">
              <div>
                <div className="text-2xl font-black text-[#F39C12] mb-1">1.</div>
                <p className="text-sm text-white/90 font-bold mb-1">We won&apos;t let him lie.</p>
                <p className="text-xs text-white/60">If he doesn&apos;t know, he says so and escalates.</p>
              </div>
              <div>
                <div className="text-2xl font-black text-[#F39C12] mb-1">2.</div>
                <p className="text-sm text-white/90 font-bold mb-1">We won&apos;t charge per token.</p>
                <p className="text-xs text-white/60">150 messages/month included, period.</p>
              </div>
              <div>
                <div className="text-2xl font-black text-[#F39C12] mb-1">3.</div>
                <p className="text-sm text-white/90 font-bold mb-1">We won&apos;t sell chat data.</p>
                <p className="text-xs text-white/60">Conversations are private. Nothing feeds model training.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MEDIA KIT — COMMISSIONABLE (SalesCrunchCRM) */}
      <section id="kit" className="px-6 py-20 border-b border-slate-200">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-1.5 text-[10px] font-bold tracking-widest text-emerald-600 uppercase mb-3">
              <CurrencyDollar size={12} weight="fill" /> Earn commissions on these
            </div>
            <h2 className="text-3xl md:text-5xl font-black mb-4 text-slate-900">
              SalesCrunchCRM media kit.
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Pre-made logos, banners, copy, and the 4 marketing pieces above
              packaged as HTML files. Click any file to download. Public URLs
              — share them anywhere.
            </p>
            <p className="text-xs text-slate-500 mt-3">
              <span className="inline-block bg-emerald-100 text-emerald-700 font-bold px-2 py-0.5 rounded">TIP</span>{' '}
              The kit zip now includes an <code className="bg-slate-100 px-1 rounded text-[11px]">index.html</code> —
              unzip and double-click it to browse the whole kit in your browser.
            </p>
          </div>

          {/* Mega download — SalesCrunchCRM kit only */}
          <div className="grid sm:grid-cols-2 gap-4 mb-10">
            <DownloadCard
              href={`${BRAND_BASE}/salescrunchcrm-media-kit.zip`}
              icon={<Package size={28} weight="fill" />}
              title="SalesCrunchCRM kit"
              sub=".zip · 452 KB · logos + banners + email swipes + 4 marketing HTML pages"
              tone="blue"
              data="aff-dl-zip-sc"
            />
            <DownloadCard
              href={`${BRAND_BASE}/full-media-kit.zip`}
              icon={<Package size={28} weight="fill" />}
              title="Full kit (both brands)"
              sub=".zip · 839 KB · everything in one archive"
              tone="emerald"
              data="aff-dl-zip-all"
            />
          </div>

          {/* Direct PNG previews for SalesCrunchCRM banners — same
              inline-preview pattern as MigrationCrunch. Affiliates can
              see the creative before downloading, then paste onto their
              own sites/Reddit/YouTube descriptions. */}
          <div className="grid sm:grid-cols-2 gap-4 mb-6" data-testid="aff-sc-png-grid">
            <AssetPreviewCard
              href={`${BRAND_BASE}/salescrunchcrm-banner-300x250.png`}
              title="Banner — 300×250 (original)"
              dimensions="300×250"
              fileSize="17 KB"
              useCase="Reddit / display — 'Fed up with slow, bloated CRMs?'"
              previewBg="dark"
              data="aff-dl-sc-banner-300x250"
            />
            <AssetPreviewCard
              href={`${BRAND_BASE}/salescrunchcrm-banner-300x250-hate-crms.png`}
              title="Banner — 300×250 · Hate CRMs angle"
              dimensions="300×250"
              fileSize="19 KB"
              useCase="Reddit — 'For people who hate bloated CRMs'"
              previewBg="dark"
              data="aff-dl-sc-banner-hate-crms"
            />
            <AssetPreviewCard
              href={`${BRAND_BASE}/salescrunchcrm-banner-300x250-nothing-extra.png`}
              title="Banner — 300×250 · Everything you need angle"
              dimensions="300×250"
              fileSize="20 KB"
              useCase="Reddit — 'Everything you need. Nothing you don't'"
              previewBg="dark"
              data="aff-dl-sc-banner-nothing-extra"
            />
            <AssetPreviewCard
              href={`${BRAND_BASE}/salescrunchcrm-banner-300x250-act-reimagined.png`}
              title="Banner — 300×250 · ACT! Reimagined angle"
              dimensions="300×250"
              fileSize="16 KB"
              useCase="Reddit — targets ACT! refugee crowd"
              previewBg="dark"
              data="aff-dl-sc-banner-act-reimagined"
            />
            <AssetPreviewCard
              href={`${BRAND_BASE}/salescrunchcrm-banner-300x250-simple-powerful.png`}
              title="Banner — 300×250 · Simple. Powerful. AI-native"
              dimensions="300×250"
              fileSize="18 KB"
              useCase="Reddit — bullet-list conversion angle"
              previewBg="dark"
              data="aff-dl-sc-banner-simple-powerful"
            />
            <AssetPreviewCard
              href={`${BRAND_BASE}/salescrunchcrm-banner-728x90.png`}
              title="Banner — 728×90 (leaderboard)"
              dimensions="728×90"
              fileSize="20 KB"
              useCase="Blog sidebars · newsletter headers"
              previewBg="dark"
              data="aff-dl-sc-banner-728x90"
            />
          </div>

          {/* SalesCrunchCRM — ALTERNATE SIZES (728×90 leaderboards + 300×600
              half-page skyscrapers) for the 4 headline angles. Same creative
              angles as the 300×250 grid above, resized for YouTube sidebars,
              blog headers, and newsletter placements where the square banner
              doesn't fit. Ships all 8 variants at once — affiliates can pick
              the size + angle combo that matches their placement. */}
          <div className="mt-8 mb-2" data-testid="aff-sc-alt-sizes">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-black mb-3">
              Alternate sizes · 728×90 leaderboards + 300×600 half-page
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <AssetPreviewCard
                href={`${BRAND_BASE}/salescrunchcrm-banner-728x90-hate-crms.png`}
                title="Banner — 728×90 · Hate CRMs angle"
                dimensions="728×90"
                fileSize="18 KB"
                useCase="Blog leaderboard · 'For people who hate bloated CRMs'"
                previewBg="dark"
                data="aff-dl-sc-banner-728-hate-crms"
              />
              <AssetPreviewCard
                href={`${BRAND_BASE}/salescrunchcrm-banner-728x90-nothing-extra.png`}
                title="Banner — 728×90 · Everything you need angle"
                dimensions="728×90"
                fileSize="18 KB"
                useCase="Newsletter header · 'Everything you need. Nothing you don't'"
                previewBg="dark"
                data="aff-dl-sc-banner-728-nothing-extra"
              />
              <AssetPreviewCard
                href={`${BRAND_BASE}/salescrunchcrm-banner-728x90-act-reimagined.png`}
                title="Banner — 728×90 · ACT! Reimagined angle"
                dimensions="728×90"
                fileSize="14 KB"
                useCase="Blog leaderboard · targets ACT! refugee crowd"
                previewBg="dark"
                data="aff-dl-sc-banner-728-act-reimagined"
              />
              <AssetPreviewCard
                href={`${BRAND_BASE}/salescrunchcrm-banner-728x90-simple-powerful.png`}
                title="Banner — 728×90 · Simple. Powerful. AI-native"
                dimensions="728×90"
                fileSize="15 KB"
                useCase="Newsletter header · bullet-list conversion angle"
                previewBg="dark"
                data="aff-dl-sc-banner-728-simple-powerful"
              />
              <AssetPreviewCard
                href={`${BRAND_BASE}/salescrunchcrm-banner-300x600-hate-crms.png`}
                title="Banner — 300×600 · Hate CRMs angle"
                dimensions="300×600"
                fileSize="35 KB"
                useCase="YouTube sidebar · 'For people who hate bloated CRMs'"
                previewBg="dark"
                data="aff-dl-sc-banner-600-hate-crms"
              />
              <AssetPreviewCard
                href={`${BRAND_BASE}/salescrunchcrm-banner-300x600-nothing-extra.png`}
                title="Banner — 300×600 · Everything you need angle"
                dimensions="300×600"
                fileSize="36 KB"
                useCase="YouTube sidebar · 'Everything you need. Nothing you don't'"
                previewBg="dark"
                data="aff-dl-sc-banner-600-nothing-extra"
              />
              <AssetPreviewCard
                href={`${BRAND_BASE}/salescrunchcrm-banner-300x600-act-reimagined.png`}
                title="Banner — 300×600 · ACT! Reimagined angle"
                dimensions="300×600"
                fileSize="31 KB"
                useCase="YouTube sidebar · targets ACT! refugee crowd"
                previewBg="dark"
                data="aff-dl-sc-banner-600-act-reimagined"
              />
              <AssetPreviewCard
                href={`${BRAND_BASE}/salescrunchcrm-banner-300x600-simple-powerful.png`}
                title="Banner — 300×600 · Simple. Powerful. AI-native"
                dimensions="300×600"
                fileSize="33 KB"
                useCase="YouTube sidebar · bullet-list conversion angle"
                previewBg="dark"
                data="aff-dl-sc-banner-600-simple-powerful"
              />
            </div>
          </div>

          {/* SalesCrunchCRM assets */}
          <KitSection
            brand="SalesCrunchCRM"
            logoHorizontal={`${BRAND_BASE}/salescrunchcrm-logo-horizontal-800.png`}
            bgClass="bg-gradient-to-br from-[#0B5394] to-[#073763]"
            slug="sc"
            assets={SC_ASSETS}
          />

          {/* AFFILIATE RECRUITMENT BANNERS — for affiliates to promote
              the affiliate program itself on their own sites. Emerald +
              amber palette signals "money" and distinguishes from the
              product banners above. */}
          <div className="mt-12 mb-2" data-testid="aff-recruitment-banners">
            <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-black mb-1">
                  For affiliate marketers · pitch the program itself
                </div>
                <h3 className="text-2xl font-black text-slate-900">Affiliate recruitment banners</h3>
                <p className="text-sm text-slate-600 mt-1">
                  Paste these on your own site, YouTube description, or Reddit
                  posts to recruit sub-affiliates. Every subscriber they land
                  is <strong>$12/mo × 60 months = $720 lifetime</strong> to
                  them (and to you, if Rewardful sub-affiliate credit is on).
                </p>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <AssetPreviewCard
                href={`${BRAND_BASE}/salescrunchcrm-affiliate-banner-300x250-lifetime.png`}
                title="Affiliate — 300×250 · Lifetime terms"
                dimensions="300×250"
                fileSize="18 KB"
                useCase="'40% recurring for 5 years · $720 lifetime'"
                previewBg="dark"
                data="aff-dl-recruit-lifetime"
              />
              <AssetPreviewCard
                href={`${BRAND_BASE}/salescrunchcrm-affiliate-banner-300x250-act-refugees.png`}
                title="Affiliate — 300×250 · ACT! refugee angle"
                dimensions="300×250"
                fileSize="20 KB"
                useCase="'Recommend the CRM ACT! users pick'"
                previewBg="dark"
                data="aff-dl-recruit-act-refugees"
              />
              <AssetPreviewCard
                href={`${BRAND_BASE}/salescrunchcrm-affiliate-banner-300x250-simple-commission.png`}
                title="Affiliate — 300×250 · Simple commission"
                dimensions="300×250"
                fileSize="19 KB"
                useCase="'Simple CRM. Simple commission. 40% × 60 months'"
                previewBg="dark"
                data="aff-dl-recruit-simple-commission"
              />
            </div>

            {/* Recruitment banners — alternate sizes (leaderboards + skyscrapers)
                for affiliates promoting the affiliate program itself. Same 3
                angles as the 300×250s above, resized for YouTube sidebars,
                blog headers, and newsletter placements. */}
            <div className="mt-6 mb-2">
              <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-black mb-3">
                Alternate sizes · 728×90 leaderboards + 300×600 half-page
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <AssetPreviewCard
                  href={`${BRAND_BASE}/salescrunchcrm-affiliate-banner-728x90-lifetime.png`}
                  title="Affiliate — 728×90 · Lifetime terms"
                  dimensions="728×90"
                  fileSize="16 KB"
                  useCase="Blog leaderboard · '40% recurring · $720 lifetime'"
                  previewBg="dark"
                  data="aff-dl-recruit-728-lifetime"
                />
                <AssetPreviewCard
                  href={`${BRAND_BASE}/salescrunchcrm-affiliate-banner-728x90-act-refugees.png`}
                  title="Affiliate — 728×90 · ACT! refugee angle"
                  dimensions="728×90"
                  fileSize="17 KB"
                  useCase="Newsletter header · 'Recommend the CRM ACT! users pick'"
                  previewBg="dark"
                  data="aff-dl-recruit-728-act-refugees"
                />
                <AssetPreviewCard
                  href={`${BRAND_BASE}/salescrunchcrm-affiliate-banner-728x90-simple-commission.png`}
                  title="Affiliate — 728×90 · Simple commission"
                  dimensions="728×90"
                  fileSize="15 KB"
                  useCase="Blog leaderboard · 'Simple CRM. Simple commission.'"
                  previewBg="dark"
                  data="aff-dl-recruit-728-simple-commission"
                />
                <AssetPreviewCard
                  href={`${BRAND_BASE}/salescrunchcrm-affiliate-banner-300x600-lifetime.png`}
                  title="Affiliate — 300×600 · Lifetime terms"
                  dimensions="300×600"
                  fileSize="33 KB"
                  useCase="YouTube sidebar · '40% recurring · $720 lifetime'"
                  previewBg="dark"
                  data="aff-dl-recruit-600-lifetime"
                />
                <AssetPreviewCard
                  href={`${BRAND_BASE}/salescrunchcrm-affiliate-banner-300x600-act-refugees.png`}
                  title="Affiliate — 300×600 · ACT! refugee angle"
                  dimensions="300×600"
                  fileSize="36 KB"
                  useCase="YouTube sidebar · 'Recommend the CRM ACT! users pick'"
                  previewBg="dark"
                  data="aff-dl-recruit-600-act-refugees"
                />
                <AssetPreviewCard
                  href={`${BRAND_BASE}/salescrunchcrm-affiliate-banner-300x600-simple-commission.png`}
                  title="Affiliate — 300×600 · Simple commission"
                  dimensions="300×600"
                  fileSize="35 KB"
                  useCase="YouTube sidebar · 'Simple CRM. Simple commission.'"
                  previewBg="dark"
                  data="aff-dl-recruit-600-simple-commission"
                />
              </div>
            </div>
          </div>

          {/* FACEBOOK / META SOCIAL ASSETS — Facebook pages need specific sizes
              or images get cropped weirdly (Chip hit this — 300×250 got squeezed
              into the circle profile crop and cut off the wordmark). These are
              the 3 sizes Meta actually uses: 1080 profile, 820×312 cover, and
              1200×630 feed post. Both brands supplied so Chip can spin up FB
              pages for either product without another request. */}
          <div className="mt-12 mb-2" data-testid="aff-facebook-assets">
            <div className="mb-5">
              <div className="text-[10px] uppercase tracking-widest text-[#1877F2] font-black mb-1">
                Facebook · Meta · sized correctly
              </div>
              <h3 className="text-2xl font-black text-slate-900">Facebook social assets</h3>
              <p className="text-sm text-slate-600 mt-1">
                Facebook crops images weirdly if you use the wrong size (profile
                = circle crop, cover = mobile edge crop). These are pre-sized
                exactly to Meta&apos;s specs so nothing gets cut off. Upload as-is.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <AssetPreviewCard
                href={`${BRAND_BASE}/migrationcrunch-fb-profile-1080.png`}
                title="FB profile · MigrationCrunch"
                dimensions="1080×1080"
                fileSize="28 KB"
                useCase="Page profile picture (FB crops to circle)"
                previewBg="dark"
                data="aff-dl-fb-mc-profile"
              />
              <AssetPreviewCard
                href={`${BRAND_BASE}/migrationcrunch-fb-cover-820x312.png`}
                title="FB cover · MigrationCrunch"
                dimensions="820×312"
                fileSize="25 KB"
                useCase="Page cover banner (mobile crops edges)"
                previewBg="dark"
                data="aff-dl-fb-mc-cover"
              />
              <AssetPreviewCard
                href={`${BRAND_BASE}/migrationcrunch-fb-feed-1200x630.png`}
                title="FB feed post · MigrationCrunch"
                dimensions="1200×630"
                fileSize="62 KB"
                useCase="Organic post image / ad creative"
                previewBg="dark"
                data="aff-dl-fb-mc-feed"
              />
              <AssetPreviewCard
                href={`${BRAND_BASE}/salescrunchcrm-fb-profile-1080.png`}
                title="FB profile · SalesCrunchCRM"
                dimensions="1080×1080"
                fileSize="40 KB"
                useCase="Page profile picture (FB crops to circle)"
                previewBg="dark"
                data="aff-dl-fb-sc-profile"
              />
              <AssetPreviewCard
                href={`${BRAND_BASE}/salescrunchcrm-fb-cover-820x312.png`}
                title="FB cover · SalesCrunchCRM"
                dimensions="820×312"
                fileSize="33 KB"
                useCase="Page cover banner (mobile crops edges)"
                previewBg="dark"
                data="aff-dl-fb-sc-cover"
              />
              <AssetPreviewCard
                href={`${BRAND_BASE}/salescrunchcrm-fb-feed-1200x630.png`}
                title="FB feed post · SalesCrunchCRM"
                dimensions="1200×630"
                fileSize="61 KB"
                useCase="Organic post image / ad creative"
                previewBg="dark"
                data="aff-dl-fb-sc-feed"
              />
            </div>
            <div className="mt-4 bg-blue-50 border border-blue-200 rounded-md p-3 text-xs text-slate-700 leading-relaxed">
              <strong className="text-[#1877F2]">Facebook upload tips:</strong>{' '}
              For the profile picture, upload the 1080×1080 as-is — Facebook
              handles the circle crop; the logo is safe-zoned inside the middle
              60%. For the cover, upload the 820×312 exactly — don&apos;t let
              Facebook resize a larger image or it&apos;ll blur. For feed posts,
              1200×630 is the landscape standard and also works as a Facebook
              Ad creative (Meta accepts 1200×628 too; the 2px difference is
              cosmetic).
            </div>
          </div>

          {/* Standalone marketing HTML downloads */}
          <div className="mt-10 border border-slate-200 rounded-lg overflow-hidden shadow-sm">
            <div className="bg-gradient-to-br from-[#0B5394] to-[#073763] px-6 py-5 flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[#F39C12] font-black mb-1">
                  Marketing pieces — as HTML
                </div>
                <h3 className="text-white text-lg font-black">Save / email / print the 4 marketing pieces</h3>
              </div>
              <span className="text-[10px] uppercase tracking-widest text-white/60 hidden md:inline">.html · open in any browser</span>
            </div>
            <div className="p-5 bg-white grid sm:grid-cols-2 md:grid-cols-4 gap-3">
              {MARKETING_HTML_ASSETS.map((a, i) => (
                <a
                  key={i}
                  href={a.href}
                  download
                  className="bg-slate-50 hover:bg-white hover:border-[#0B5394] border border-slate-200 rounded-md p-3 transition-colors flex items-start gap-3"
                  data-testid={`aff-dl-mkt-${a.testid}`}
                >
                  <div className="text-[#0B5394] flex-shrink-0">
                    <FileText size={18} weight="fill" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm text-slate-900 leading-tight">{a.label}</div>
                    <div className="text-[11px] text-slate-500 mt-0.5">{a.sub}</div>
                  </div>
                  <Download size={14} weight="bold" className="text-slate-400 flex-shrink-0 mt-1" />
                </a>
              ))}
            </div>
          </div>

          <p className="text-center text-xs text-slate-500 mt-10">
            Brand guidelines:{' '}
            <a href={`${BRAND_BASE}/brand-guidelines.html`} className="underline text-[#0B5394] hover:text-[#073763]" data-testid="aff-dl-guidelines">
              brand-guidelines.html
            </a>{' '}
            · Need a format we didn&apos;t include? Email{' '}
            <a href="mailto:affiliates@migrationcrunch.com" className="underline text-[#0B5394] hover:text-[#073763]">affiliates@migrationcrunch.com</a>
          </p>
        </div>
      </section>

      {/* MIGRATIONCRUNCH PROGRAM — COMING SOON + WAITLIST + BRAND-ONLY ASSETS
          Chip's decision: MigrationCrunch may or may not need a formal
          affiliate plan. Product may sell itself (one-time purchase, no clear
          #2 in market, consultants can mark it up themselves). Waitlist is a
          demand probe — if signups accumulate, Chip builds a program; if not,
          he ships without one. Brand-mention assets stay available regardless
          so anyone writing about MC has proper logos + banners. */}
      {/* VIDEOCRUNCH PROGRAM — 40% of the $49/mo plan AND every credit pack.
          The free first video (no credit card) makes this the lowest-friction
          referral of the three products. */}
      <section id="videocrunch-program" className="px-6 py-20 border-b border-slate-200 bg-gradient-to-b from-[#0a0e27] to-[#101636] text-white" data-testid="aff-vcx-program">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-1.5 text-[10px] font-bold tracking-widest text-emerald-400 uppercase mb-3">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400"></span>
              </span>
              VideoCrunch Affiliate Program · Live · 40% of everything
            </div>
            <h2 className="text-3xl md:text-5xl font-black mb-5 text-white leading-tight">
              The easiest referral in SaaS: <span className="text-[#F39C12]">the first video is free.</span>
            </h2>
            <p className="text-base md:text-lg text-white/70 max-w-2xl mx-auto leading-relaxed">
              VideoCrunch makes AI tutorial videos recorded on the customer&apos;s
              real software, with machine-verified narration (patent pending).
              Their first video costs nothing — no credit card — so your
              audience actually clicks. Then you earn <strong className="text-white">40%
              of everything they ever spend</strong>: the $49/mo plan and every
              credit pack, for 60 months.
            </p>
          </div>

          {/* The math — three cards */}
          <div className="grid sm:grid-cols-3 gap-4 max-w-5xl mx-auto mb-12">
            <div className="bg-white/5 border border-white/10 rounded-lg p-5 text-center" data-testid="aff-vcx-math-sub">
              <div className="text-3xl font-black text-[#F39C12] mb-1">$19.60<span className="text-base text-white/50">/mo</span></div>
              <div className="text-[10px] uppercase tracking-widest font-black text-white/50 mb-2">The $49/mo plan</div>
              <p className="text-xs text-white/70 leading-relaxed">
                40% of every monthly invoice, month after month, for up to 60
                months per customer.
              </p>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-lg p-5 text-center" data-testid="aff-vcx-math-packs">
              <div className="text-3xl font-black text-[#F39C12] mb-1">40%</div>
              <div className="text-[10px] uppercase tracking-widest font-black text-white/50 mb-2">Every credit pack</div>
              <p className="text-xs text-white/70 leading-relaxed">
                Credits never expire, so customers keep topping up — and every
                top-up pays you 40%. No caps.
              </p>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-lg p-5 text-center" data-testid="aff-vcx-math-hook">
              <div className="text-3xl font-black text-emerald-400 mb-1">$0</div>
              <div className="text-[10px] uppercase tracking-widest font-black text-white/50 mb-2">The hook</div>
              <p className="text-xs text-white/70 leading-relaxed">
                First video free, up to 2 minutes, no credit card. Promote a
                genuine freebie — the product converts from there.
              </p>
            </div>
          </div>

          {/* Who it's for */}
          <div className="max-w-3xl mx-auto mb-12 bg-white/5 border border-white/10 rounded-lg p-6" data-testid="aff-vcx-audience">
            <div className="text-[10px] uppercase tracking-widest font-black text-[#F39C12] mb-3">
              Who converts best
            </div>
            <ul className="text-sm text-white/80 space-y-2 leading-relaxed">
              <li>• <strong className="text-white">SaaS YouTubers &amp; newsletter writers</strong> — every software company in your audience needs tutorial videos and hates making them</li>
              <li>• <strong className="text-white">Dev-tool communities</strong> — videos regenerate when the UI changes; the scripts double as end-to-end tests</li>
              <li>• <strong className="text-white">Agencies &amp; onboarding consultants</strong> — deliver client tutorial libraries without recording a single take</li>
            </ul>
          </div>

          {/* Ready-made banners — same self-serve pattern as the MC media kit */}
          <div className="max-w-5xl mx-auto mb-12">
            <div className="text-center mb-6">
              <div className="text-[10px] uppercase tracking-widest font-black text-[#F39C12] mb-1">
                VideoCrunch media kit
              </div>
              <p className="text-sm text-white/60">
                Ready-made display banners — click any to open full-size,
                right-click to save. Retina versions: add <code className="text-white/80">@2x</code> before <code className="text-white/80">.png</code>.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
              <AssetPreviewCard
                href="/brand/videocrunch-logo-horizontal-800.png"
                title="Logo — Horizontal (Dark)"
                dimensions="800×200 (also 400/1600 + SVG)"
                fileSize="19 KB"
                useCase="Dark backgrounds / headers"
                data="aff-vcx-logo-dark"
              />
              <AssetPreviewCard
                href="/brand/videocrunch-logo-horizontal-light-800.png"
                title="Logo — Horizontal (Light)"
                dimensions="800×200 (also 400/1600 + SVG)"
                fileSize="19 KB"
                useCase="Light backgrounds / print"
                data="aff-vcx-logo-light"
              />
              <AssetPreviewCard
                href="/brand/videocrunch-logo-stacked-512.png"
                title="Logo — Stacked (Square)"
                dimensions="512×512 (also 128/256 + SVG)"
                fileSize="23 KB"
                useCase="Avatars / app icons / YouTube"
                data="aff-vcx-logo-stacked"
              />
              <AssetPreviewCard
                href="/brand/videocrunch-logo-mono-light-512.png"
                title="Logo — Mono (White)"
                dimensions="512×512 transparent (+ dark ver.)"
                fileSize="12 KB"
                useCase="Watermarks / single-color uses"
                data="aff-vcx-logo-mono"
              />
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <AssetPreviewCard
                href="/brand/videocrunch-banner-300x250.png"
                title="Banner — Make Themselves"
                dimensions="300×250 (+@2x)"
                fileSize="17 KB"
                useCase="Sidebar / display ads"
                data="aff-vcx-banner-1"
              />
              <AssetPreviewCard
                href="/brand/videocrunch-banner-300x250-never-wrong.png"
                title="Banner — Never Wrong"
                dimensions="300×250 (+@2x)"
                fileSize="17 KB"
                useCase="The verification angle"
                data="aff-vcx-banner-2"
              />
              <AssetPreviewCard
                href="/brand/videocrunch-banner-300x250-ui-changed.png"
                title="Banner — UI Changed"
                dimensions="300×250 (+@2x)"
                fileSize="18 KB"
                useCase="The regeneration angle"
                data="aff-vcx-banner-3"
              />
              <AssetPreviewCard
                href="/brand/videocrunch-banner-728x90.png"
                title="Leaderboard"
                dimensions="728×90 (+@2x)"
                fileSize="16 KB"
                useCase="Blog headers / newsletters"
                data="aff-vcx-banner-4"
              />
            </div>
          </div>

          <div className="text-center">
            <a
              href={buildApplyUrl('vcx_apply')}
              className="px-7 py-3.5 bg-[#F39C12] hover:bg-[#E08C00] text-black font-black rounded-md inline-flex items-center justify-center gap-2"
              data-testid="aff-vcx-apply"
            >
              Apply free — one link covers all three products <ArrowRight size={16} weight="bold" />
            </a>
            <p className="text-xs text-white/50 mt-3">
              Existing affiliates: your current link already tracks VideoCrunch
              purchases. Nothing to set up.
            </p>
          </div>
        </div>
      </section>

      <section id="migrationcrunch-program" className="px-6 py-20 border-b border-slate-200 bg-gradient-to-b from-[#0F0F1A] to-[#1a1a2e] text-white" data-testid="aff-mc-program">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-1.5 text-[10px] font-bold tracking-widest text-emerald-400 uppercase mb-3">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400"></span>
              </span>
              MigrationCrunch Affiliate Program · Live · 40% per sale
            </div>
            <h2 className="text-3xl md:text-5xl font-black mb-5 text-white leading-tight">
              Earn <span className="text-emerald-400">$239.60</span> every time someone switches CRMs.
            </h2>
            <p className="text-base md:text-lg text-white/70 max-w-2xl mx-auto leading-relaxed">
              MigrationCrunch is a one-time-purchase AI CRM migration tool with
              patent-pending note extraction — $599, done in minutes, no close
              #2 in the market. You keep <strong className="text-white">40% of
              every sale</strong>. And because migrated customers usually
              subscribe to SalesCrunchCRM next, your 40% recurring kicks in on
              top — same link, same customer, both payouts.
            </p>
          </div>

          {/* Apply CTA — one Rewardful application covers all three products */}
          <div className="max-w-xl mx-auto mb-12 text-center">
            <a
              href={buildApplyUrl('mc_apply')}
              className="px-7 py-3.5 bg-emerald-500 hover:bg-emerald-400 text-black font-black rounded-md inline-flex items-center justify-center gap-2"
              data-testid="aff-mc-apply"
            >
              Apply free — one link covers all three products <ArrowRight size={16} weight="bold" />
            </a>
            <p className="text-xs text-white/50 mt-3">
              Already a SalesCrunchCRM affiliate? You&apos;re in — your existing
              link tracks MigrationCrunch sales automatically.
            </p>
          </div>

          {/* Pitch — why it sells + the consultant math */}
          <div className="grid sm:grid-cols-2 gap-4 max-w-4xl mx-auto mb-14">
            <div className="bg-white/5 border border-white/10 rounded-lg p-5" data-testid="aff-mc-pitch-tool">
              <div className="text-[10px] uppercase tracking-widest font-black text-emerald-400 mb-2">
                Why the tool sells itself
              </div>
              <ul className="text-sm text-white/80 space-y-2 leading-relaxed">
                <li>• Minutes to run — not the 2-week consultant nightmare</li>
                <li>• 99% accuracy on structured + unstructured fields</li>
                <li>• Free tier: 50 contacts <em>with</em> full AI note extraction — buyer sees it work on their own data</li>
                <li>• Crunchy AI concierge walks the buyer through the whole migration</li>
                <li>• Cheaper than every competitor doing anything close</li>
              </ul>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-lg p-5" data-testid="aff-mc-pitch-math">
              <div className="text-[10px] uppercase tracking-widest font-black text-emerald-400 mb-2">
                The math (especially for consultants)
              </div>
              <ul className="text-sm text-white/80 space-y-2 leading-relaxed">
                <li>• $599 × 40% = <strong className="text-white">$239.60 per referred sale</strong>, paid once</li>
                <li>• IT consultants: stop charging $5k for migrations that take weeks — refer the $599 tool, keep the relationship, collect the commission</li>
                <li>• Cross-sell built in: if they then subscribe to SalesCrunchCRM ($30/user/mo), you also earn $12/mo for 60 months from the same customer</li>
                <li>• 90-day cookie · Net-30 payouts via PayPal or Wise</li>
              </ul>
            </div>
          </div>

          {/* Brand-only assets — MC banners for anyone writing about the product */}
          <div className="border-t border-white/10 pt-10">
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-1.5 text-[10px] font-bold tracking-widest text-white/60 uppercase mb-3">
                <Package size={12} weight="fill" /> Brand-only assets · use freely
              </div>
              <h3 className="text-2xl md:text-3xl font-black mb-3 text-white">
                MigrationCrunch media kit
              </h3>
              <p className="text-sm text-white/60 max-w-2xl mx-auto leading-relaxed">
                Whether or not we launch a formal affiliate plan, if you&apos;re
                writing about MigrationCrunch (podcast, Reddit, blog, newsletter,
                YouTube) — grab the logos and banners below. No permission
                required. Just don&apos;t change the wordmark.
              </p>
            </div>

          {/* Direct PNG downloads WITH inline previews — affiliates can see
              exactly what they're getting before clicking download. Solves
              the "I downloaded the zip, now I have to inspect every file"
              complaint. */}
          <div className="grid sm:grid-cols-2 gap-4 mb-6" data-testid="aff-mc-png-grid">
            <AssetPreviewCard
              href={`${BRAND_BASE}/migrationcrunch-banner-300x250.png`}
              title="Banner — 300×250 (v1)"
              dimensions="300×250"
              fileSize="18 KB"
              useCase="Reddit / Google Display — '20 years of notes' angle"
              previewBg="dark"
              data="aff-dl-mc-banner-300x250"
            />
            <AssetPreviewCard
              href={`${BRAND_BASE}/migrationcrunch-banner-300x250-v2.png`}
              title="Banner — 300×250 (v2)"
              dimensions="300×250"
              fileSize="18 KB"
              useCase="Reddit / Google Display — 'notes & history move with you' angle"
              previewBg="dark"
              data="aff-dl-mc-banner-300x250-v2"
            />
            <AssetPreviewCard
              href={`${BRAND_BASE}/migrationcrunch-banner-728x90.png`}
              title="Banner — 728×90"
              dimensions="728×90"
              fileSize="15 KB"
              useCase="Leaderboard / top-of-page banner"
              previewBg="dark"
              data="aff-dl-mc-banner-728x90"
            />
            <AssetPreviewCard
              href={`${BRAND_BASE}/migrationcrunch-banner-300x250-act-sync.png`}
              title="Banner — 300×250 · ACT! Sync angle"
              dimensions="300×250"
              fileSize="18 KB"
              useCase="Reddit — targets ACT! 2018 sync failures"
              previewBg="dark"
              data="aff-dl-mc-banner-act-sync"
            />
            <AssetPreviewCard
              href={`${BRAND_BASE}/migrationcrunch-banner-300x250-vs-agency.png`}
              title="Banner — 300×250 · Agency price angle"
              dimensions="300×250"
              fileSize="17 KB"
              useCase="Reddit — 'Don't pay $5,000 to an agency'"
              previewBg="dark"
              data="aff-dl-mc-banner-vs-agency"
            />
            <AssetPreviewCard
              href={`${BRAND_BASE}/migrationcrunch-banner-300x250-history-move.png`}
              title="Banner — 300×250 · History angle"
              dimensions="300×250"
              fileSize="15 KB"
              useCase="Reddit — 'Your CRM history isn't stuck'"
              previewBg="dark"
              data="aff-dl-mc-banner-history-move"
            />
            <AssetPreviewCard
              href={`${BRAND_BASE}/migrationcrunch-banner-300x250-buried-notes.png`}
              title="Banner — 300×250 · Buried notes angle"
              dimensions="300×250"
              fileSize="17 KB"
              useCase="Reddit — Patent AI extracts buried notes"
              previewBg="dark"
              data="aff-dl-mc-banner-buried-notes"
            />
            <AssetPreviewCard
              href={`${BRAND_BASE}/migrationcrunch-favicon-64.png`}
              title="Favicon — 64×64"
              dimensions="64×64"
              fileSize="2 KB"
              useCase="Small square · profile pic · OG icon"
              previewBg="light"
              data="aff-dl-mc-favicon"
            />
            <AssetPreviewCard
              href={`${BRAND_BASE}/migrationcrunch-logo-horizontal-800.png`}
              title="Logo — horizontal 800px"
              dimensions="800×~200"
              fileSize="20 KB"
              useCase="Email signatures · partner pages · slide decks"
              previewBg="dark"
              data="aff-dl-mc-logo-horizontal"
            />
          </div>

          {/* MigrationCrunch — ALTERNATE SIZES (728×90 leaderboards + 300×600
              half-page skyscrapers) for the 4 headline angles. Same 4 hooks
              as the 300×250 grid above (act-sync / buried-notes / vs-agency /
              history-move), resized for YouTube sidebars, blog leaderboards,
              and newsletter headers where the square banner won't fit. */}
          <div className="mt-2 mb-2" data-testid="aff-mc-alt-sizes">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-black mb-3">
              Alternate sizes · 728×90 leaderboards + 300×600 half-page
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <AssetPreviewCard
                href={`${BRAND_BASE}/migrationcrunch-banner-728x90-act-sync.png`}
                title="Banner — 728×90 · ACT! Sync angle"
                dimensions="728×90"
                fileSize="15 KB"
                useCase="Blog leaderboard · targets ACT! 2018 sync failures"
                previewBg="dark"
                data="aff-dl-mc-banner-728-act-sync"
              />
              <AssetPreviewCard
                href={`${BRAND_BASE}/migrationcrunch-banner-728x90-buried-notes.png`}
                title="Banner — 728×90 · Buried notes angle"
                dimensions="728×90"
                fileSize="14 KB"
                useCase="Newsletter header · patent AI extracts buried notes"
                previewBg="dark"
                data="aff-dl-mc-banner-728-buried-notes"
              />
              <AssetPreviewCard
                href={`${BRAND_BASE}/migrationcrunch-banner-728x90-vs-agency.png`}
                title="Banner — 728×90 · Agency price angle"
                dimensions="728×90"
                fileSize="15 KB"
                useCase="Blog leaderboard · 'Don't pay $5,000 to an agency'"
                previewBg="dark"
                data="aff-dl-mc-banner-728-vs-agency"
              />
              <AssetPreviewCard
                href={`${BRAND_BASE}/migrationcrunch-banner-728x90-history-move.png`}
                title="Banner — 728×90 · History angle"
                dimensions="728×90"
                fileSize="14 KB"
                useCase="Newsletter header · 'Your CRM history isn't stuck'"
                previewBg="dark"
                data="aff-dl-mc-banner-728-history-move"
              />
              <AssetPreviewCard
                href={`${BRAND_BASE}/migrationcrunch-banner-300x600-act-sync.png`}
                title="Banner — 300×600 · ACT! Sync angle"
                dimensions="300×600"
                fileSize="32 KB"
                useCase="YouTube sidebar · targets ACT! 2018 sync failures"
                previewBg="dark"
                data="aff-dl-mc-banner-600-act-sync"
              />
              <AssetPreviewCard
                href={`${BRAND_BASE}/migrationcrunch-banner-300x600-buried-notes.png`}
                title="Banner — 300×600 · Buried notes angle"
                dimensions="300×600"
                fileSize="31 KB"
                useCase="YouTube sidebar · patent AI extracts buried notes"
                previewBg="dark"
                data="aff-dl-mc-banner-600-buried-notes"
              />
              <AssetPreviewCard
                href={`${BRAND_BASE}/migrationcrunch-banner-300x600-vs-agency.png`}
                title="Banner — 300×600 · Agency price angle"
                dimensions="300×600"
                fileSize="32 KB"
                useCase="YouTube sidebar · 'Don't pay $5,000 to an agency'"
                previewBg="dark"
                data="aff-dl-mc-banner-600-vs-agency"
              />
              <AssetPreviewCard
                href={`${BRAND_BASE}/migrationcrunch-banner-300x600-history-move.png`}
                title="Banner — 300×600 · History angle"
                dimensions="300×600"
                fileSize="29 KB"
                useCase="YouTube sidebar · 'Your CRM history isn't stuck'"
                previewBg="dark"
                data="aff-dl-mc-banner-600-history-move"
              />
            </div>
          </div>

          {/* MigrationCrunch assets */}
          <KitSection
            brand="MigrationCrunch"
            logoHorizontal={`${BRAND_BASE}/migrationcrunch-logo-horizontal-800.png`}
            bgClass="bg-[#0F0F1A]"
            slug="mc"
            assets={MC_ASSETS}
          />
          </div> {/* /border-t brand-only wrapper */}
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="px-6 py-20 bg-slate-50 border-b border-slate-200">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-5xl font-black mb-4 text-slate-900">
              Affiliate FAQ
            </h2>
            <p className="text-slate-600">
              Common questions. Anything missing — email{' '}
              <a href="mailto:affiliates@migrationcrunch.com" className="underline text-[#0B5394]">
                affiliates@migrationcrunch.com
              </a>
              .
            </p>
          </div>
          <div className="space-y-3">
            {FAQS.map((f, i) => (
              <details
                key={i}
                className="group bg-white border border-slate-200 rounded-md p-4 shadow-sm"
                data-testid={`aff-faq-${i}`}
              >
                <summary className="cursor-pointer font-bold text-slate-900 text-sm flex items-start gap-2 list-none">
                  <span className="text-[#F39C12]">Q.</span>
                  <span className="flex-1">{f.q}</span>
                  <span className="text-slate-400 text-xs group-open:rotate-45 transition-transform">+</span>
                </summary>
                <div className="mt-3 text-sm text-slate-700 leading-relaxed pl-5">
                  {f.a}
                </div>
              </details>
            ))}
          </div>
          <p className="text-center mt-8 text-sm text-slate-600">
            Full legal terms:{' '}
            <Link to="/affiliate-agreement" className="text-[#0B5394] underline hover:text-[#073763]" data-testid="aff-legal-link">
              Affiliate Program Agreement
            </Link>
          </p>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="px-6 py-20 bg-gradient-to-b from-[#0B5394] to-[#073763] text-white text-center">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl md:text-5xl font-black mb-4 leading-tight">
            Promote a CRM people <span className="text-[#F39C12]">actually thank you for</span>.
          </h2>
          <p className="text-white/80 mb-9 text-base md:text-lg max-w-2xl mx-auto">
            Most affiliate programs ask you to sell something complicated to
            people who don&apos;t want it. We&apos;re asking you to point fed-up users
            toward the simple, fast, AI-powered CRM they&apos;ve been waiting for.
          </p>
          <a
            href={buildApplyUrl('bottom_apply')}
            className="px-8 py-4 bg-[#F39C12] hover:bg-[#E08C00] text-black font-black rounded-md inline-flex items-center gap-2 text-lg"
            data-testid="aff-bottom-apply"
          >
            Apply free — get your link
            <ArrowRight size={18} weight="bold" />
          </a>
          <p className="text-xs text-white/40 mt-6">
            Questions before you sign up?{' '}
            <a href="mailto:affiliates@migrationcrunch.com" className="text-white/70 hover:text-white underline">
              affiliates@migrationcrunch.com
            </a>
          </p>
        </div>
      </section>

      {/* Mini footer */}
      <footer className="px-6 py-8 text-center text-xs text-slate-500 bg-white">
        <p>
          © {new Date().getFullYear()} SalesCrunchCRM ·{' '}
          <Link to="/" className="hover:text-[#0B5394]" data-testid="aff-footer-home">Home</Link>{' '}
          ·{' '}
          <Link to="/affiliate-agreement" className="hover:text-[#0B5394]" data-testid="aff-footer-agreement">Affiliate Agreement</Link>{' '}
          ·{' '}
          <Link to="/privacy" className="hover:text-[#0B5394]" data-testid="aff-footer-privacy">Privacy</Link>{' '}
          ·{' '}
          <Link to="/terms" className="hover:text-[#0B5394]" data-testid="aff-footer-terms">Terms</Link>
        </p>
      </footer>

      {/* Crunchy the salesperson — answers affiliate questions + writes swipe copy */}
      <SalesConciergeFloat />
    </div>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────

function EarnCard({ count, total, sub, highlight, data }) {
  return (
    <div
      className={`rounded-lg p-6 text-center transition-shadow ${
        highlight
          ? 'bg-[#0B5394] text-white shadow-lg ring-2 ring-[#F39C12]'
          : 'bg-white text-slate-900 border border-slate-200 shadow-sm hover:shadow-md'
      }`}
      data-testid={data}
    >
      <div className={`text-[10px] uppercase tracking-widest mb-1 ${highlight ? 'text-[#F39C12]' : 'text-slate-500'}`}>
        Refer
      </div>
      <div className="text-5xl font-black mb-2">{count}</div>
      <div className={`text-2xl font-black mb-1 ${highlight ? 'text-[#F39C12]' : 'text-[#0B5394]'}`}>
        {total}
      </div>
      <div className={`text-[11px] ${highlight ? 'text-white/70' : 'text-slate-500'}`}>{sub}</div>
    </div>
  );
}

function Cell({ mode, text, winner }) {
  const Icon =
    mode === 'good' ? (
      <CheckCircle size={14} weight="fill" className={winner ? 'text-[#F39C12]' : 'text-emerald-600'} />
    ) : mode === 'bad' ? (
      <XIcon size={14} weight="bold" className="text-rose-500" />
    ) : null;
  return (
    <div className={`text-xs inline-flex items-start gap-1.5 leading-snug ${winner ? 'font-bold text-slate-900' : 'text-slate-700'}`}>
      {Icon}
      <span>{text}</span>
    </div>
  );
}

function Pillar({ icon, title, body, tone }) {
  const toneMap = {
    amber: 'bg-amber-50 border-amber-200 text-amber-700',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-700',
    purple: 'bg-purple-50 border-purple-200 text-purple-700',
    blue: 'bg-blue-50 border-blue-200 text-[#0B5394]',
  };
  return (
    <div className={`border rounded-lg p-5 ${toneMap[tone].split(' ').slice(0, 2).join(' ')}`}>
      <div className={toneMap[tone].split(' ').slice(2).join(' ') + ' mb-3'}>{icon}</div>
      <h3 className="text-base font-black mb-1.5 text-slate-900">{title}</h3>
      <p className="text-xs text-slate-700 leading-relaxed">{body}</p>
    </div>
  );
}

function Step({ n, title, body }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm">
      <div className="w-9 h-9 rounded-full bg-[#F39C12] text-black font-black text-base flex items-center justify-center mb-3">
        {n}
      </div>
      <h3 className="text-base font-black mb-1.5 text-slate-900">{title}</h3>
      <p className="text-xs text-slate-600 leading-relaxed">{body}</p>
    </div>
  );
}

/**
 * MCWaitlistForm — email capture for the MigrationCrunch affiliate program
 * "coming soon" section. Chip is still deciding whether to build the program
 * at all; this form is a demand probe. Submissions flow to
 * POST /api/affiliates/mc-waitlist which persists to Mongo and pings
 * chip@migrationcrunch.com via Resend so he sees signups accumulate live.
 *
 * Optional `audience` field lets signups say who they'd promote to
 * (consultants, other CRM refugees, etc.) — informs whether the program
 * would target B2B resellers or individual affiliates.
 */
function MCWaitlistForm() {
  const [email, setEmail] = useState('');
  const [audience, setAudience] = useState('');
  const [companyWebsite, setCompanyWebsite] = useState(''); // honeypot
  const [status, setStatus] = useState('idle'); // idle | submitting | success | already | error
  const [errorMsg, setErrorMsg] = useState('');

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setStatus('submitting');
    setErrorMsg('');
    try {
      const res = await axios.post(`${API_URL}/api/affiliates/mc-waitlist`, {
        email: email.trim(),
        audience: audience.trim(),
        company_website: companyWebsite,
      });
      if (res.data?.already_signed_up) {
        setStatus('already');
      } else {
        setStatus('success');
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setErrorMsg(typeof detail === 'string' ? detail : 'Something went wrong. Please try again.');
      setStatus('error');
    }
  };

  if (status === 'success' || status === 'already') {
    return (
      <div
        className="bg-emerald-500/10 border-2 border-emerald-400/40 rounded-xl p-6 text-center"
        data-testid="aff-mc-waitlist-success"
      >
        <CheckCircle size={36} weight="fill" className="text-emerald-400 mx-auto mb-3" />
        <div className="text-xl font-black text-white mb-1">
          {status === 'already' ? "You're already on the list." : "You're on the list."}
        </div>
        <p className="text-sm text-white/70 leading-relaxed">
          If we launch the MigrationCrunch affiliate program, you&apos;ll be the
          first to hear. In the meantime, feel free to grab the media kit
          below and start writing about the product.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="bg-white/5 border border-white/10 rounded-xl p-6 md:p-7 backdrop-blur-sm"
      data-testid="aff-mc-waitlist-form"
    >
      <div className="text-center mb-5">
        <div className="text-[10px] uppercase tracking-widest font-black text-amber-400 mb-2">
          Notify me when it launches
        </div>
        <div className="text-lg md:text-xl font-black text-white leading-tight">
          Interested? Get on the waitlist.
        </div>
        <p className="text-xs text-white/60 mt-2 leading-relaxed">
          We&apos;ll only email you if / when the program opens. No spam. Ever.
        </p>
      </div>

      {/* Honeypot — hidden from users, visible to bots */}
      <input
        type="text"
        name="company_website"
        value={companyWebsite}
        onChange={(e) => setCompanyWebsite(e.target.value)}
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', opacity: 0 }}
      />

      <div className="space-y-3">
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="your@email.com"
          className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-md text-white placeholder-white/40 focus:outline-none focus:border-amber-400 focus:bg-white/15 transition-colors"
          data-testid="aff-mc-waitlist-email"
          disabled={status === 'submitting'}
        />
        <input
          type="text"
          value={audience}
          onChange={(e) => setAudience(e.target.value)}
          placeholder="Who would you promote to? (optional — e.g. CRM consultants)"
          maxLength={200}
          className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-md text-white placeholder-white/40 focus:outline-none focus:border-amber-400 focus:bg-white/15 transition-colors text-sm"
          data-testid="aff-mc-waitlist-audience"
          disabled={status === 'submitting'}
        />
        <button
          type="submit"
          disabled={status === 'submitting' || !email.trim()}
          className="w-full bg-amber-400 hover:bg-amber-300 disabled:bg-amber-400/40 disabled:cursor-not-allowed text-black font-black rounded-md px-5 py-3 transition-colors inline-flex items-center justify-center gap-2"
          data-testid="aff-mc-waitlist-submit"
        >
          {status === 'submitting' ? 'Adding you to the list...' : (
            <>Join the waitlist <ArrowRight size={16} weight="bold" /></>
          )}
        </button>
        {status === 'error' && (
          <p className="text-xs text-red-300 text-center" data-testid="aff-mc-waitlist-error">
            {errorMsg}
          </p>
        )}
      </div>
    </form>
  );
}



function DownloadCard({ href, icon, title, sub, tone, data }) {
  const toneMap = {
    blue: 'bg-gradient-to-br from-[#0B5394] to-[#073763] text-white',
    amber: 'bg-[#0F0F1A] text-white',
    emerald: 'bg-gradient-to-br from-emerald-700 to-emerald-900 text-white',
  };
  return (
    <a
      href={href}
      download
      className={`${toneMap[tone]} rounded-lg p-6 flex items-center gap-4 hover:opacity-90 transition-opacity shadow-md`}
      data-testid={data}
    >
      <div className="text-[#F39C12] flex-shrink-0">{icon}</div>
      <div className="flex-1 min-w-0">
        <div className="font-black text-base">{title}</div>
        <div className="text-xs text-white/70 mt-0.5">{sub}</div>
      </div>
      <Download size={20} weight="bold" className="text-white/80 flex-shrink-0" />
    </a>
  );
}

/**
 * AssetPreviewCard — renders the actual PNG/SVG inline so affiliates can SEE
 * what they're downloading before they download it. Solves the "I downloaded
 * the zip, now I have to open every file to figure out what it looks like"
 * complaint. Used for the 4 hero PNG banner+logo assets on the affiliates page.
 *
 * The preview area uses a soft checkered background (visible only where the
 * asset has transparency) so logos with no padding still read clearly. The
 * download is enforced via the browser `download` attribute on the explicit
 * "Download PNG" button, so clicking the preview opens the raw image in a
 * new tab (handy if the affiliate wants to right-click → copy image directly).
 */
function AssetPreviewCard({ href, title, dimensions, fileSize, useCase, previewBg = 'dark', data }) {
  // Checkered transparency pattern as a CSS background — built in so we don't
  // need to ship a separate image asset for the preview background.
  const checkered = {
    backgroundImage:
      'linear-gradient(45deg, #e2e8f0 25%, transparent 25%), ' +
      'linear-gradient(-45deg, #e2e8f0 25%, transparent 25%), ' +
      'linear-gradient(45deg, transparent 75%, #e2e8f0 75%), ' +
      'linear-gradient(-45deg, transparent 75%, #e2e8f0 75%)',
    backgroundSize: '14px 14px',
    backgroundPosition: '0 0, 0 7px, 7px -7px, -7px 0px',
  };
  // For dark-background logos, show them against the MigrationCrunch dark tile
  // so transparency reads correctly. Light-background assets use checkered.
  const previewStyle = previewBg === 'dark' ? { background: '#0F0F1A' } : checkered;

  return (
    <div
      className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm flex flex-col"
      data-testid={data}
    >
      {/* Inline preview of the actual image */}
      <div
        className="flex items-center justify-center p-5 min-h-[160px] border-b border-slate-200"
        style={previewStyle}
      >
        <a href={href} target="_blank" rel="noopener noreferrer" title="Open full-size in new tab">
          <img
            src={href}
            alt={title}
            className="max-w-full max-h-[200px] object-contain"
            loading="lazy"
            data-testid={`${data}-img`}
          />
        </a>
      </div>

      {/* Metadata + download button */}
      <div className="p-4 flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <div className="font-black text-sm text-slate-900">{title}</div>
          <div className="text-[11px] text-slate-500 mt-0.5">
            {dimensions} · PNG · {fileSize}
          </div>
          <div className="text-[11px] text-slate-400 mt-0.5">{useCase}</div>
        </div>
        <a
          href={href}
          download
          className="bg-[#F39C12] hover:bg-[#E08C00] text-black font-bold rounded-md px-3 py-2 text-xs inline-flex items-center gap-1.5 transition-colors flex-shrink-0"
          data-testid={`${data}-btn`}
        >
          <Download size={14} weight="bold" /> PNG
        </a>
      </div>
    </div>
  );
}

function KitSection({ brand, logoHorizontal, bgClass, slug, assets }) {
  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden shadow-sm">
      <div className={`${bgClass} px-6 py-5 flex items-center justify-between`}>
        <img src={logoHorizontal} alt={brand} className="h-9 md:h-11 w-auto" />
        <span className="text-[10px] uppercase tracking-widest text-white/60">Brand assets</span>
      </div>
      <div className="p-5 bg-white grid sm:grid-cols-2 md:grid-cols-3 gap-3">
        {assets.map((a, i) => (
          <a
            key={i}
            href={a.href}
            download
            className="bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-md p-3 flex items-center gap-3 transition-colors"
            data-testid={`aff-dl-${slug}-${a.testid}`}
          >
            <div className="text-[#0B5394] flex-shrink-0">{a.icon}</div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm text-slate-900 truncate">{a.label}</div>
              <div className="text-[11px] text-slate-500">{a.sub}</div>
            </div>
            <Download size={14} weight="bold" className="text-slate-400 flex-shrink-0" />
          </a>
        ))}
      </div>
    </div>
  );
}

// ─── Data ─────────────────────────────────────────────────────────────────

// ─── Sales Toolkit data ───────────────────────────────────────────────────

const OBJECTIONS = [
  {
    q: 'I already have Salesforce.',
    a: 'What do you actually use 80% of the time? Contacts, activities, a pipeline, a search bar. That\u2019s us — at 1/6 the price, opens 5x faster, and your team can actually use it without a week of training.',
  },
  {
    q: 'Why not HubSpot?',
    a: 'Because you don\u2019t need marketing automation. You need to remember that John called Tuesday and promised a callback. That\u2019s a $30 problem, not a $1,200/mo one.',
  },
  {
    q: 'What happened to ACT!?',
    a: 'ACT! was acquired, bloated, and priced out of its own audience. We rebuilt the parts you actually used — on modern tech, in the browser, with team sync and email built in.',
  },
  {
    q: 'Can I migrate my old database?',
    a: 'Yes. Export your old CRM to CSV, upload it. Contacts, activities, and custom fields come over in minutes. Or use our sister product MigrationCrunch for the messy cases (notes, .dbf files, 250k+ records).',
  },
];

const ACT_ISSUES = [
  {
    problem: 'ACT! runs slowly — screens take ages, reports lag.',
    fix: 'MongoDB on cloud infrastructure with auto-tuned indexes. Server-side pagination. No SQL Server on your desktop to fragment. No plugins to pile up. Performance fixes ship continuously without you doing anything.',
    talkingPoint: 'The slow-down problem in ACT! is fundamentally about running SQL Server on a desktop. We removed SQL Server entirely.',
  },
  {
    problem: 'Syncing issues with Outlook, Gmail, or mobile.',
    fix: 'Single source of truth. There is one database — no sync to break. Real-time WebSocket updates push changes to every open browser in under a second. Mobile is just the website on a phone. No separate sync engine.',
    talkingPoint: 'There\u2019s no sync because there\u2019s nothing to sync. One database, one truth, broadcast in real time.',
  },
  {
    problem: 'Freezes or crashes — won\u2019t open, needs ActDiag.',
    fix: 'The "app" is your browser. If your browser works, SalesCrunchCRM works. No service packs, no preferences folder, no database repair tool. "Reset" is a tab reload. Stale-cache protection built in.',
    talkingPoint: 'There\u2019s nothing to crash on your machine. Worst case, you reload the tab.',
  },
  {
    problem: '\u201CCannot open the database\u201D / \u201CSQL Server not running.\u201D',
    fix: 'Database is managed in cloud infrastructure with health checks, auto-restart, and replication. No .ADF file path. Indexes managed automatically. Live System Stats panel lets admins verify health themselves.',
    talkingPoint: 'You will never type services.msc again.',
  },
  {
    problem: 'Outlook integration breaks every update — buy Act4Outlook to fix.',
    fix: 'Email is built into the product. No Outlook, no plugin, no add-in. Mail merge \u2192 personalized send \u2192 activity log all happens server-side via Resend. Works identically in Chrome on Mac, Edge on Windows, Safari on iPad.',
    talkingPoint: 'Email is a feature, not a third-party add-on you re-buy and re-fix every quarter.',
  },
  {
    problem: '\u201CYou do not have permission to access this record.\u201D',
    fix: 'Shared database by default — everyone sees all contacts, which is what teams actually want. Role-based permissions (admin / manager / user) changeable in one click. Public/private contact toggle for sensitive ones. Full audit trail per record.',
    talkingPoint: 'Permissions designed for how teams work in 2026, not for a 1996 file-locked database.',
  },
  {
    problem: 'Backups failing — restoring from .ADF and .ALF files.',
    fix: 'Automatic infrastructure-level MongoDB backups. Plus a one-click Workspace Backup that exports every admin config (fields, templates, groups) as a JSON file. Audit log preserves contact history per record.',
    talkingPoint: 'You will never copy an .ADF file again. Backup is something the system does, not something you do.',
  },
];

const FEATURES = [
  { name: 'Contact Card (3-column ACT! layout)', benefit: 'Every contact on one screen — business card, address, status, notes. No hunting through tabs.' },
  { name: 'Contact List', benefit: 'Sortable, paginated. Handles 250,000+ contacts without slowing down.' },
  { name: 'Boolean Lookup', benefit: '8 operators. \u201CEveryone in Texas with \u2018Tech\u2019 in their company, not called in 90 days.\u201D' },
  { name: 'Global Keyword Search', benefit: 'Type anything — find it. Even inside a 3-year-old note.' },
  { name: 'Activity Log', benefit: 'Every call, email, meeting, note timestamped per contact. Perfect memory.' },
  { name: 'Recurring Activities', benefit: 'Schedule once, repeats up to 52 instances. Weekly check-ins on autopilot.' },
  { name: 'Calendar (Day/Week/Month)', benefit: 'Your whole schedule at a glance. Drag, click, done.' },
  { name: 'Task List', benefit: 'Daily battle plan. What to do, who to call, in priority order.' },
  { name: 'Sales Pipeline (Kanban)', benefit: '6-stage drag-and-drop. See every dollar in motion.' },
  { name: 'Relationship Mapping', benefit: 'Link contacts (colleague, manager, referral, vendor\u2026). \u201CWho referred this client?\u201D one click.' },
  { name: 'Groups', benefit: 'Customers, prospects, event attendees. Mail merge a group in one click.' },
  { name: 'Custom User Fields (6 renameable)', benefit: 'Bend the CRM to your industry. Insurance? Rename to \u201CPolicy #\u201D.' },
  { name: 'Reports (5 types)', benefit: 'Contact, Phone List, Directory, Activity History, History Summary. Print-ready, 2 clicks.' },
  { name: 'Templates with merge fields', benefit: 'Write it once. Send it 1,000 times — personalized.' },
  { name: 'Bulk Mail Merge + Send', benefit: 'ACT! 4.0 mail merge, except it actually ends up in inboxes.' },
  { name: 'Label & Envelope Printing', benefit: 'Avery 5160/5161/5162/5163 labels. #10/A2/A7 envelopes. Holiday cards write themselves.' },
  { name: 'Real-Time Collaboration', benefit: 'See teammates editing live. No more \u201Cwho has the latest on this account?\u201D' },
  { name: 'Shared Database (ACT! 4.0 style)', benefit: 'Everyone sees everything. Private toggle on individual contacts for sensitive ones.' },
  { name: 'Quick Add (Cmd/Ctrl+K)', benefit: 'New prospect calls — you\u2019re typing before they finish saying their name.' },
  { name: 'Import / Export (CSV)', benefit: 'Switch in an afternoon, not a quarter. Free CSV import from any CRM.' },
  { name: 'Role-Based Access', benefit: 'Admin / Manager / User. Your assistant can\u2019t accidentally delete the CEO\u2019s contacts.' },
  { name: 'Google Sign-In', benefit: 'One click. No new password to remember.' },
  { name: 'Email Sending (Resend)', benefit: 'Send personalized emails one-off or bulk. Auto-logs to activity history.' },
];

const CRUNCHY_DIFFS = [
  {
    icon: Brain,
    title: 'He knows the product cold.',
    body: 'Fed a curated knowledge base every conversation. Knows exactly which features exist, where they live in the sidebar, the actual prices, the actual refund policy. When he doesn\u2019t know, he says so.',
  },
  {
    icon: HandHeart,
    title: 'He has hands, not just a mouth.',
    body: 'Searches contacts, finds stale leads, schedules calls, drafts + sends bulk emails with your approval. One chat command replaces 30 minutes of CRM busywork.',
  },
  {
    icon: Target,
    title: 'He thinks one step further.',
    body: 'Reproduce \u2192 workaround \u2192 hypothesize \u2192 escalate. Not "click \u2018Need a human?\u2019 below" — actual diagnosis like a senior support rep.',
  },
  {
    icon: Lightning,
    title: 'He streams answers in real time.',
    body: 'No 15-second spinning circle. Words flow as he generates them. Same total time, dramatically better feel.',
  },
  {
    icon: ChatCircleDots,
    title: 'He escalates cleanly to humans.',
    body: 'Hit "Need a human?" \u2192 entire transcript auto-sent to support. They show up already up to speed. No "let me catch up on your case" delay.',
  },
  {
    icon: CurrencyDollar,
    title: 'He respects your monthly budget.',
    body: '150 messages/month included free. No surprise bills, no per-token gouging. Crunchy warns you cleanly when you\u2019re near the limit.',
  },
];

// ─── Original page data ───────────────────────────────────────────────────

const COMPARE_ROWS = [
  {
    label: 'Monthly price',
    us: { mode: 'good', text: '$30/user — no upsells, no tiers' },
    hubspot: { mode: 'bad', text: '$90+/seat (Sales Pro)' },
    salesforce: { mode: 'bad', text: '$80–$300/seat' },
    zoho: { mode: 'bad', text: '$20–$60/seat + add-ons' },
  },
  {
    label: 'Learning curve',
    us: { mode: 'good', text: 'Open it and start working. ACT! 4.0 muscle memory works.' },
    hubspot: { mode: 'bad', text: 'Days of onboarding + paid certifications' },
    salesforce: { mode: 'bad', text: '2–6 weeks with a consultant' },
    zoho: { mode: 'bad', text: 'Steep — modules, profiles, picklists' },
  },
  {
    label: 'Speed at 250k contacts',
    us: { mode: 'good', text: 'Instant — production-tested' },
    hubspot: { mode: 'bad', text: 'Slows past 50k records' },
    salesforce: { mode: 'bad', text: 'Custom indexing required' },
    zoho: { mode: 'bad', text: 'Pagination workarounds' },
  },
  {
    label: 'AI agent included',
    us: { mode: 'good', text: 'Yes — drafts emails, finds stale leads, bulk-with-approval' },
    hubspot: { mode: 'bad', text: 'AI is a paid add-on' },
    salesforce: { mode: 'bad', text: 'Einstein = enterprise tier only' },
    zoho: { mode: 'bad', text: 'Zia is limited + paid' },
  },
  {
    label: 'CRM migration support',
    us: { mode: 'good', text: 'Free SalesCrunchCRM import + MigrationCrunch sister product' },
    hubspot: { mode: 'bad', text: '$3,000 onboarding fees common' },
    salesforce: { mode: 'bad', text: '$10k+ via partner consultancy' },
    zoho: { mode: 'bad', text: 'DIY or paid concierge' },
  },
  {
    label: 'Commission to you',
    us: { mode: 'good', text: '40% recurring for 60 months ($720/customer)' },
    hubspot: { mode: 'bad', text: '$0–$1,000 one-time per partner tier' },
    salesforce: { mode: 'bad', text: 'No public affiliate program' },
    zoho: { mode: 'bad', text: '10–15% one-time' },
  },
];

const FAQS = [
  {
    q: 'How exactly does the $720 per customer math work?',
    a: 'SalesCrunchCRM costs $30 per user per month. We pay 40% of every monthly invoice for the first 60 months of that customer\'s subscription. $30 × 40% × 60 = $720. Paid monthly, not all at once. If a referred customer cancels at month 18, you got 18 monthly commissions ($216 total) and the meter stops — fair and transparent.',
  },
  {
    q: 'What\'s the cookie duration?',
    a: '90 days. If someone clicks your link, leaves, comes back 89 days later via a different channel, and signs up — you still get credit.',
  },
  {
    q: 'How and when do I get paid?',
    a: 'Net-30 via PayPal or Wise. We pay out on the 1st of each month for commissions earned the previous month, as long as you\'ve cleared the $50 minimum payout threshold (rolls over if not).',
  },
  {
    q: 'What happens to my commission if my referral asks for a refund?',
    a: 'SalesCrunchCRM has a 30-day money-back guarantee. If a referral refunds within 30 days, that commission is reversed. After 30 days, refunds don\'t affect commissions already paid.',
  },
  {
    q: 'Can I run paid ads on Google / Facebook?',
    a: 'Yes, with limits. You may NOT bid on our branded keywords ("salescrunchcrm", "sales crunch crm", "migrationcrunch", or close variants) — that just makes us bid against you for traffic that was already coming to us. Non-branded keywords ("CRM alternative", "ACT! replacement", etc.) are fair game. Full details in the Affiliate Agreement.',
  },
  {
    q: 'Do I need a website or audience?',
    a: 'Helpful but not required. Some of our best affiliates are people running niche newsletters, vertical Slack communities, or consultants who recommend tools to clients. Email marketers, YouTubers, and CRM consultants all do well.',
  },
  {
    q: 'Does one link really cover all three products?',
    a: 'Yes. SalesCrunchCRM, MigrationCrunch, and VideoCrunch all bill through the same payment system, so your single referral link tracks all of them. If your referral tries VideoCrunch for free today and buys a $599 migration next quarter, both commissions are yours — you acquired the customer once, you earn on everything they buy.',
  },
  {
    q: 'Can I promote MigrationCrunch too?',
    a: 'Yes — it\'s live at 40%. MigrationCrunch is a one-time $599 purchase, so you earn $239.60 per referred sale, paid once. Bonus: customers who migrate usually subscribe to SalesCrunchCRM right after, which starts your 40% recurring on top. Same link, same customer, both payouts.',
  },
  {
    q: 'How do VideoCrunch commissions work?',
    a: 'You earn 40% of everything a referred VideoCrunch customer spends: the $49/mo subscription ($19.60/mo to you) AND every one-time credit pack they buy, for 60 months from their first purchase. Their first video is free with no credit card required, which makes it the easiest click to earn in the whole program.',
  },
  {
    q: 'What can I NOT do?',
    a: 'No spam, no fake reviews, no impersonating SalesCrunchCRM or its founder, no bidding on branded keywords, no incentivized signups (e.g., "get a $5 gift card for signing up"), no false claims about features. Affiliate Agreement spells it all out — read it before you start.',
  },
  {
    q: 'I have a question that\'s not here.',
    a: <>Email <a href="mailto:affiliates@migrationcrunch.com" className="underline text-[#0B5394]">affiliates@migrationcrunch.com</a> — real humans, usually replies same-day.</>,
  },
];

// Standalone HTML versions of the 4 marketing pieces (regenerated by
// /app/scripts/build_kit_html.py). Each opens in any browser when
// downloaded — no Markdown viewer or text editor needed.
const MARKETING_HTML_ASSETS = [
  { label: 'One-page pitch', sub: 'Hero hooks · 3-line pitch · objections', href: `${BRAND_BASE}/marketing-one-pager.html`, testid: 'one-pager' },
  { label: '7 ACT! problems', sub: 'ACT! complaints → our answers', href: `${BRAND_BASE}/marketing-7-act-issues.html`, testid: '7-issues' },
  { label: 'Feature catalog', sub: 'All 23 features + benefits', href: `${BRAND_BASE}/marketing-feature-catalog.html`, testid: 'features' },
  { label: 'Why Crunchy is different', sub: 'Our AI moat explained', href: `${BRAND_BASE}/marketing-why-crunchy.html`, testid: 'crunchy' },
];

// Asset listings — every file lives in /public/brand/
const SC_ASSETS = [
  { label: 'Logo — stacked SVG', sub: 'Square, full color', href: `${BRAND_BASE}/salescrunchcrm-logo-stacked.svg`, icon: <ImageIcon size={18} weight="fill" />, testid: 'logo-stacked-svg' },
  { label: 'Logo — horizontal SVG', sub: 'Wide, dark background', href: `${BRAND_BASE}/salescrunchcrm-logo-horizontal.svg`, icon: <ImageIcon size={18} weight="fill" />, testid: 'logo-horiz-svg' },
  { label: 'Logo — horizontal light', sub: 'Wide, white background', href: `${BRAND_BASE}/salescrunchcrm-logo-horizontal-light.svg`, icon: <ImageIcon size={18} weight="fill" />, testid: 'logo-horiz-light-svg' },
  { label: 'Logo — mono dark', sub: 'Single-color B&W', href: `${BRAND_BASE}/salescrunchcrm-logo-mono-dark.svg`, icon: <ImageIcon size={18} weight="fill" />, testid: 'logo-mono-dark' },
  { label: 'Logo PNG — 512px', sub: 'Square, retina-ready', href: `${BRAND_BASE}/salescrunchcrm-logo-stacked-512.png`, icon: <ImageIcon size={18} weight="fill" />, testid: 'logo-512' },
  { label: 'Logo PNG — 256px', sub: 'Square, standard', href: `${BRAND_BASE}/salescrunchcrm-logo-stacked-256.png`, icon: <ImageIcon size={18} weight="fill" />, testid: 'logo-256' },
  { label: 'Banner 300×250', sub: 'IAB Medium Rectangle (SVG)', href: `${BRAND_BASE}/salescrunchcrm-banner-300x250.svg`, icon: <ImageIcon size={18} weight="fill" />, testid: 'banner-300' },
  { label: 'Banner 728×90', sub: 'IAB Leaderboard (SVG)', href: `${BRAND_BASE}/salescrunchcrm-banner-728x90.svg`, icon: <ImageIcon size={18} weight="fill" />, testid: 'banner-728' },
  { label: 'Banner 160×600', sub: 'IAB Wide Skyscraper (SVG)', href: `${BRAND_BASE}/salescrunchcrm-banner-160x600.svg`, icon: <ImageIcon size={18} weight="fill" />, testid: 'banner-160' },
  { label: 'Banner PNGs (all sizes)', sub: '300×250 · 728×90 · 160×600 + @2x', href: `${BRAND_BASE}/salescrunchcrm-banner-300x250@2x.png`, icon: <ImageIcon size={18} weight="fill" />, testid: 'banner-pngs' },
  { label: 'Email swipes & copy', sub: 'Subjects, bodies, Twitter, LinkedIn', href: `${BRAND_BASE}/salescrunchcrm-email-swipes.md`, icon: <FileText size={18} weight="fill" />, testid: 'swipes' },
  { label: 'Full SalesCrunchCRM .zip', sub: '419 KB — every file above', href: `${BRAND_BASE}/salescrunchcrm-media-kit.zip`, icon: <Package size={18} weight="fill" />, testid: 'kit-zip' },
];

const MC_ASSETS = [
  { label: 'Logo — stacked SVG', sub: 'Square, full color', href: `${BRAND_BASE}/migrationcrunch-logo-stacked.svg`, icon: <ImageIcon size={18} weight="fill" />, testid: 'logo-stacked-svg' },
  { label: 'Logo — horizontal SVG', sub: 'Wide, dark background', href: `${BRAND_BASE}/migrationcrunch-logo-horizontal.svg`, icon: <ImageIcon size={18} weight="fill" />, testid: 'logo-horiz-svg' },
  { label: 'Logo — horizontal light', sub: 'Wide, white background', href: `${BRAND_BASE}/migrationcrunch-logo-horizontal-light.svg`, icon: <ImageIcon size={18} weight="fill" />, testid: 'logo-horiz-light-svg' },
  { label: 'Logo — mono dark', sub: 'Single-color B&W', href: `${BRAND_BASE}/migrationcrunch-logo-mono-dark.svg`, icon: <ImageIcon size={18} weight="fill" />, testid: 'logo-mono-dark' },
  { label: 'Logo PNG — 512px', sub: 'Square, retina-ready', href: `${BRAND_BASE}/migrationcrunch-logo-stacked-512.png`, icon: <ImageIcon size={18} weight="fill" />, testid: 'logo-512' },
  { label: 'Logo PNG — 256px', sub: 'Square, standard', href: `${BRAND_BASE}/migrationcrunch-logo-stacked-256.png`, icon: <ImageIcon size={18} weight="fill" />, testid: 'logo-256' },
  { label: 'Banner 300×250', sub: 'IAB Medium Rectangle (SVG)', href: `${BRAND_BASE}/migrationcrunch-banner-300x250.svg`, icon: <ImageIcon size={18} weight="fill" />, testid: 'banner-300' },
  { label: 'Banner 728×90', sub: 'IAB Leaderboard (SVG)', href: `${BRAND_BASE}/migrationcrunch-banner-728x90.svg`, icon: <ImageIcon size={18} weight="fill" />, testid: 'banner-728' },
  { label: 'Banner 160×600', sub: 'IAB Wide Skyscraper (SVG)', href: `${BRAND_BASE}/migrationcrunch-banner-160x600.svg`, icon: <ImageIcon size={18} weight="fill" />, testid: 'banner-160' },
  { label: 'Banner PNGs (all sizes)', sub: '300×250 · 728×90 · 160×600 + @2x', href: `${BRAND_BASE}/migrationcrunch-banner-300x250@2x.png`, icon: <ImageIcon size={18} weight="fill" />, testid: 'banner-pngs' },
  { label: 'Email swipes & copy', sub: 'Subjects, bodies, Twitter, LinkedIn', href: `${BRAND_BASE}/migrationcrunch-email-swipes.md`, icon: <FileText size={18} weight="fill" />, testid: 'swipes' },
  { label: 'Full MigrationCrunch .zip', sub: '385 KB — every file above', href: `${BRAND_BASE}/migrationcrunch-media-kit.zip`, icon: <Package size={18} weight="fill" />, testid: 'kit-zip' },
];
